// [S-410] ChatPane — the agentic chat surface over real platform tools
// (certified templates + precedent database), reused by Home (the main
// chat-first landing, S-666) and by DraftDetail's Chat panel (S-517).
// Originally the body of the standalone Studio Agents screen/tab-picker
// (`/agents`), retired once in-app routing (route_to_agent, S-666) + the
// `assistant` persona's own inline review tools made manually picking a
// persona tab redundant — every specialist is reachable from a single
// conversation now. This module is what actually survived that removal.
// Built on assistant-ui headless primitives (no Tailwind) styled with
// @csai/design-system tokens; wired to the admin /chat proxy.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useComposer,
  useComposerRuntime,
  useExternalStoreRuntime,
  useMessage,
  useMessagePartText,
  type AppendMessage,
  type ComposerState,
  type ThreadMessageLike,
} from "@assistant-ui/react";
import { markdownToHtml } from "../../lib/markdown";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, LoadingState, Select, Spinner } from "../../components";
import { getApiErrorMessage } from "../../data/api";
import { DRAFTS_KEY, useDraft, type Draft } from "../../data/draftsQuery";
import {
  ContextPicker,
  type ContextPickerKind,
  type PickedRecord,
} from "./ContextPicker";
import { draftCardState } from "./draftCardState";
import {
  CHAT_KEY,
  draftGeneratedFromMessage,
  elicitFromMessage,
  messageText,
  useChatCancel,
  useChatMessageStream,
  useChatRetry,
  useChatSessionDetail,
  useResetActiveAgent,
  useReviewDecide,
  useReviewRevise,
  useReviewGoto,
  useReviewResolveSkips,
  useUploadChatAttachment,
  type ActiveRecord,
  type ChatAgentKind,
  type ChatMessageDTO,
  type ChatStreamEvent,
  type ElicitField,
  type RecordRef,
  type ReviewStepCurrent,
  type ReviewStepEvent,
} from "../../data/chatQuery";
import { useRecordDrawer } from "./RecordDrawer";
import { setAgentTurnRunning } from "./agentDraftLock";
import {
  ReviewPane,
  InlineReview,
  reviewInlineEnabled,
  reviewCompactEnabled,
  setReviewFocus,
  scrollToClauseNow,
  htmlToText,
} from "./review";

/** Dedupe RecordRefs by uri, first-seen order (mirrors core dedupeRefs). */
function dedupeByUri(refs: RecordRef[]): RecordRef[] {
  const seen = new Set<string>();
  const out: RecordRef[] = [];
  for (const r of refs) {
    if (seen.has(r.uri)) continue;
    seen.add(r.uri);
    out.push(r);
  }
  return out;
}

export function TenantPrompt({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="sa__tenant-prompt">
      <label htmlFor="sa-tenant">Organization</label>
      <p>
        Precedent search is per-studio. Enter the studio id (e.g. <code>pinnacle</code>)
        to scope this chat.
      </p>
      <input
        id="sa-tenant"
        className="sa__tenant-input"
        value={value}
        placeholder="studio id"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// [S-507] An in-chat confirmation request the agent attached to a turn.
type Elicitation = { tool: string; prompt: string; fields: ElicitField[] };
// [S-516] A generated draft the assistant turn links to, rendered as an
// "Open in Draft Workspace" card. [S-669] action distinguishes a brand-new
// draft from an in-place revision so the card's subtitle can say which.
type GeneratedDraft = {
  draftId: string;
  title: string;
  url: string;
  action: "created" | "updated";
  /** [S-798] The content is still generating after the reply; the card polls. */
  status?: "building";
  /** [S-802] WHICH generation this card announced (the build's startedAt).
   *  Every card for a draft shares its id, so without this a retry's build is
   *  read by the earlier cards too and they spin again beside the live one. */
  startedAt?: string;
};

type UIMsg = {
  id: string;
  role: "user" | "assistant";
  text: string;
  references?: RecordRef[];
  // [S-650] Files the user attached WITH this turn — rendered inline as chips on
  // the user message (claude.ai style).
  attachments?: Array<{ id: string; filename: string }>;
  // [context-picker] Records attached from the template / draft pickers.
  picked?: Array<{ kind: "draft" | "template"; id: string; title: string }>;
  // [S-507] When set, the assistant turn renders an inline confirmation form.
  elicit?: Elicitation;
  // [S-516] When set, the assistant turn renders an "Open in Draft Workspace" card.
  draftGenerated?: GeneratedDraft;
  // [chat-regenerate] When this user turn was persisted (ISO). Drives the hover
  // timestamp on the message; absent on a just-sent turn (shown as "just now").
  createdAt?: string;
};

// [S-507] Lets the per-message confirmation form submit its answer back through
// the thread's normal send path (the form is rendered deep inside assistant-ui
// primitives, so it reaches sendText via context — same pattern as the record
// drawer).
type ElicitPicks = { draftIds: string[]; records: Array<{ kind: "draft"; id: string; title: string }> };
const ElicitSubmitContext = createContext<(text: string, picks?: ElicitPicks) => void>(() => {});
function useElicitSubmit(): (text: string, picks?: ElicitPicks) => void {
  return useContext(ElicitSubmitContext);
}

// [rail epic] Lets <MessageAttachments> (also rendered deep inside
// assistant-ui's own message loop, so it can't receive props either) resolve
// an attachment chip to the session id it needs to build an openable
// RecordRef — same reasoning, same pattern as ElicitSubmitContext above.
const ChatSessionIdContext = createContext<string>("");
function useChatSessionId(): string {
  return useContext(ChatSessionIdContext);
}

// [chat-regenerate] Lets <UserMessage> (rendered deep inside assistant-ui's
// message loop) show the hover action row — timestamp · copy · retry — and fire
// a retry. Same context pattern as ElicitSubmitContext above. `lastUserId` is the
// UIMsg id of the LAST user turn; only that turn offers Retry (re-answering an
// earlier turn would orphan everything below it — a branching feature we haven't
// built). `busy` hides Retry while a turn is streaming.
type RetryCtx = {
  lastUserId: string | null;
  busy: boolean;
  onRetry: () => void;
};
const RetryContext = createContext<RetryCtx>({
  lastUserId: null,
  busy: false,
  onRetry: () => {},
});
function useRetry(): RetryCtx {
  return useContext(RetryContext);
}

export function ChatPane({
  sessionId,
  tenantId,
  initialMessage,
  openRecordRef,
  draftHost = false,
}: {
  sessionId: string;
  tenantId: string | undefined;
  // [S-487] When set, the first turn is sent automatically once the (empty)
  // conversation mounts — lets the Home welcome composer create a session and
  // fire its opening question in one step. ChatThread guards it: sent once, and
  // only into an empty conversation (so reopening a chat never re-sends).
  initialMessage?: string;
  // [rail epic] What the caller's own record rail has open right now, if it
  // has one at all — undefined (the default) means this ChatPane mount has
  // no rail (e.g. DraftDetail's Chat panel), and every turn leaves the
  // session's activeRecord untouched. Home passes its rail's live state here.
  openRecordRef?: ActiveRecord | null;
  // True when this pane is the chat rail of the Draft Workspace: the draft under
  // review is already the page's main editor, so the review neither auto-opens
  // nor jumps into the record drawer (that would mount a second copy).
  draftHost?: boolean;
}): JSX.Element {
  // Loads the session + messages exactly like Studio Agents — so attachments
  // (the 📎 pill) and the agent's tool calls behave identically. The earlier
  // "skip the fetch for a fresh session" optimization left the session stale
  // (no attachment pill, broken read_context_file); the active-tenant fix
  // removed the race it was working around, so the normal fetch is correct.
  const detail = useChatSessionDetail(sessionId, tenantId);
  if (detail.isLoading) return <LoadingState label="Loading conversation…" />;
  if (detail.isError)
    return (
      <Alert label="Couldn't load this conversation" tone="error">
        {getApiErrorMessage(detail.error) ?? "Unknown error"}
      </Alert>
    );

  const seed: UIMsg[] = (detail.data?.messages ?? [])
    // [chat-regenerate] An interrupted turn persists as the "_[stopped]_" /
    // "_[interrupted]_" marker (markTurnInterrupted). It's a storage breadcrumb
    // (keeps model-request alternation intact for a later turn), never shown:
    // drop it from the transcript entirely. A stopped turn then reads as the user
    // message with no reply, and Retry lives on that message (hover).
    .filter((m: ChatMessageDTO) => {
      const raw = messageText(m);
      return raw !== "_[stopped]_" && raw !== "_[interrupted]_";
    })
    .map((m: ChatMessageDTO, i: number, all: ChatMessageDTO[]) => {
      // [S-516] Restore the "Open in Draft Workspace" card on reload from the
      // persisted generate_draft tool result (the live event fires only once).
      const draftGenerated = draftGeneratedFromMessage(m);
      // [elicit-reload] A form is still open only when it is the LAST turn —
      // an earlier one was answered by the message after it.
      const elicit = i === all.length - 1 ? elicitFromMessage(m) : undefined;
      const attachments = m.attachments ?? [];
      // [context-picker] Records attached from the pickers. This is the RELOAD
      // path — a separate mapping from convertMessage (the live one), and
      // patching only that one left the chip on screen until you refreshed.
      const picked = m.picked ?? [];
      return {
        id: m.id,
        role: m.role,
        text: messageText(m),
        references: m.references ?? [],
        createdAt: m.createdAt,
        // [S-650] Files sent with this turn, rendered inline on the message.
        ...(attachments.length > 0 ? { attachments } : {}),
        ...(picked.length > 0 ? { picked } : {}),
        ...(draftGenerated !== undefined ? { draftGenerated } : {}),
        ...(elicit !== undefined ? { elicit } : {}),
      };
    })
    // Keep a turn that has text, an attachment, or a picked record (any of the
    // three can be the whole turn — an empty message that just adds context).
    .filter(
      (m) =>
        m.text.length > 0 ||
        (m.attachments?.length ?? 0) > 0 ||
        (m.picked?.length ?? 0) > 0 ||
        "elicit" in m
    );
  const title = detail.data?.session.title ?? "Chat";
  const kind = detail.data?.session.agentKind;
  // [S-509d] If this session has a review in progress, seed the pane from the
  // persisted snapshot so reopening restores the current clause + all prior
  // decisions without waiting for a live `review_step`. Re-attach the `type`
  // discriminant so it's the same shape the stream handler stores. Null clears
  // the pane (no active review / switched to a plain chat).
  const activeReview: ReviewStepEvent | null =
    detail.data?.activeReview != null
      ? { type: "review_step", ...detail.data.activeReview }
      : null;
  const agentLabel = agentKindLabel(kind);
  // [S-666e] Visible chip when an in-app routing handoff is live for this
  // session — activeAgentKind (S-666a) differs from the pinned agentKind.
  // Always computed from the fetched session, never assumed, so a stale
  // client can't show a chip the backend disagrees with (no-fake-UI).
  const routedKind = detail.data?.session.activeAgentKind ?? null;
  const routedLabel =
    routedKind !== null && routedKind !== kind
      ? agentKindLabel(routedKind)
      : null;

  return (
    <ChatThread
      sessionId={sessionId}
      tenantId={tenantId}
      seed={seed}
      initialReview={activeReview}
      title={title}
      agentLabel={agentLabel}
      routedLabel={routedLabel}
      openRecordRef={openRecordRef}
      draftHost={draftHost}
      {...(initialMessage !== undefined ? { initialMessage } : {})}
    />
  );
}

// Human label for the routing chip ("actually answered by X") — the
// specialist labels mirror packages/core's agentRegistry.ts (DRAFTING_LABEL
// etc.), duplicated here since the web app doesn't depend on core directly.
const SPECIALIST_LABELS: Record<Exclude<ChatAgentKind, "assistant">, string> = {
  analysis: "Contract Analysis + Precedent Check",
  drafting: "Contract Drafting",
  longform: "Long-Form Drafting",
  negotiation: "Negotiation Expert",
};
function agentKindLabel(kind: ChatAgentKind | undefined): string {
  if (kind === undefined) return "Studio Agent";
  if (kind === "assistant") return "ContractStudio AI Assistant";
  return SPECIALIST_LABELS[kind] ?? "Studio Agent";
}

// [S-650] Send button that stays enabled when the composer is empty but a file
// is staged — so "attach + Send with no message" adds the file to the context
// with no instruction. Must render inside <ComposerPrimitive.Root> so the
// composer hooks resolve. Text present → the composer's normal send (→ onNew);
// empty + staged file → the attach-only turn.
// [bugfix] Selector kept at MODULE scope, not inlined at the call site. It is
// part of useSyncExternalStore's getSnapshot identity — a fresh arrow per render
// makes React re-read the store every render, which is half of the bug below.
const selectComposerText = (c: ComposerState): string => c.text;

function ComposerSend({
  readyCount,
  running,
  uploading,
  onEmptySend,
  onStop,
}: {
  readyCount: number;
  running: boolean;
  /** [upload race] True while ANY picked file is still uploading. */
  uploading: boolean;
  onEmptySend: () => void;
  // [chat-stop] Interrupt the in-flight turn. When running, the Send button
  // becomes Stop so the user is never stuck waiting on the agent.
  onStop: () => void;
}): JSX.Element {
  // [bugfix] Was `useComposer()` with NO selector, which React warned about on
  // every render: "The result of getSnapshot should be cached to avoid an
  // infinite loop". Bare useComposer() falls through to `identity(getState())`,
  // and the composer runtime builds a FRESH state object per call — so
  // useSyncExternalStore compared two never-equal objects with Object.is and
  // re-rendered forever, in the composer, on every keystroke. Selecting a
  // primitive fixes it: `text` is a string, so Object.is settles it and the
  // re-render stops. Only `text` was ever used here anyway.
  const composerText = useComposer(selectComposerText);
  const runtime = useComposerRuntime();
  const hasText = composerText.trim().length > 0;
  // [bugfix, reported live] `uploading` was NOT part of this condition, so with
  // text typed the button stayed live while files were still in flight. Sending
  // then took only the attachments that happened to have RESOLVED — often none
  // — and the rest landed on the session after the turn had already started.
  // ChatService snapshots session.attachments once per turn, so those files
  // were invisible for the whole turn: the agent either answered with no
  // context at all or hit read_context_file's not-found path and told the user
  // it couldn't reach a file they had just watched upload. Worst with several
  // files at once, which is exactly when people attach a deal's whole thread.
  // [chat-stop] While a turn is running, the primary control is Stop — one click
  // aborts the streaming reply and frees the composer. It replaces Send (rather
  // than sitting beside it) so there's always exactly one primary action.
  if (running) {
    return (
      <Button
        type="button"
        variant="bare"
        className="sa-composer__send sa-composer__stop"
        data-testid="sa-composer-stop"
        title="Stop the agent"
        onClick={onStop}
      >
        Stop
      </Button>
    );
  }
  const enabled = (hasText || readyCount > 0) && !uploading;
  return (
    <Button
      type="button"
      variant="bare"
      className="sa-composer__send"
      disabled={!enabled}
      data-testid="sa-composer-send"
      // Say WHY it's disabled — a dead Send button with no explanation reads as
      // a broken app, which is how this bug felt from the outside.
      title={uploading ? "Waiting for the attachment to finish uploading…" : undefined}
      onClick={() => {
        if (hasText) {
          runtime.send();
        } else if (readyCount > 0) {
          onEmptySend();
        }
      }}
    >
      {uploading ? "Uploading…" : "Send"}
    </Button>
  );
}

function ChatThread({
  sessionId,
  tenantId,
  seed,
  initialReview,
  title,
  agentLabel,
  routedLabel,
  initialMessage,
  openRecordRef,
  draftHost,
}: {
  sessionId: string;
  tenantId: string | undefined;
  seed: UIMsg[];
  // [S-509d] The persisted review to restore the pane from on (re)open, or null.
  initialReview: ReviewStepEvent | null;
  title: string;
  agentLabel: string;
  // [S-666e] Non-null when an in-app routing handoff is live — the specialist
  // persona currently answering, distinct from agentLabel (the pinned tab).
  routedLabel: string | null;
  initialMessage?: string;
  // [rail epic] See ChatPane's prop of the same name.
  openRecordRef?: ActiveRecord | null;
  draftHost: boolean;
}): JSX.Element {
  const [msgs, setMsgs] = useState<UIMsg[]>(seed);
  const [running, setRunning] = useState(false);
  // [chat-stop] The in-flight turn's abort handle, so the Stop button can cancel
  // the streaming request. Null when no turn is running.
  const abortRef = useRef<AbortController | null>(null);
  const [tool, setTool] = useState<string | null>(null);
  // [S-666e] Manual override for a routing handoff — see the chip in the header.
  const resetActiveAgent = useResetActiveAgent();
  // [S-509b] Latest clause-by-clause review payload for this session. Each
  // `review_step` event supersedes the previous one; the right-hand review pane
  // (and the in-chat decision log + ghost card) render from this.
  // [S-509d] Seeded from the persisted `activeReview` so reopening a chat mid-
  // review restores the pane (current clause, progress, prior decisions); this
  // ChatThread remounts per session (ChatPane is keyed by session id), so a
  // session with no active review starts null and the pane stays hidden.
  const [review, setReview] = useState<ReviewStepEvent | null>(initialReview);
  // [review-compact] True once the user has acted on the review in this view; the
  // next clause's card then scrolls into view (a reload must not jump).
  const [reviewActed, setReviewActed] = useState(false);
  // [async-review] The session-detail query POLLS while a walkthrough plan is
  // still building in the background (chatQuery's refetchInterval), so `initialReview`
  // updates under us. Adopt that update ONLY when it ADVANCES a still-building
  // review (building → in_progress/complete/failed) or is for a different session
  // — same-status polls are ignored (no identity thrash), and once the plan has
  // landed the live `review_step` stream + the deterministic pane mutations own
  // `review`, so we never clobber a decision the user just made.
  useEffect(() => {
    if (initialReview === null) return;
    setReview((prev) => {
      if (prev === null || prev.sessionId !== initialReview.sessionId) {
        return initialReview;
      }
      if (prev.status === "building" && initialReview.status !== "building") {
        return initialReview;
      }
      return prev;
    });
  }, [initialReview]);
  // Stacked progress trace of the agent's tool actions for the current turn
  // (so an agentic analysis shows "Reading the document → Checking the standard
  // → …" instead of flickering intermediate prose that gets replaced).
  const [steps, setSteps] = useState<string[]>([]);
  const stream = useChatMessageStream();
  // [chat-regenerate] Re-answer the last user turn after a Stop (the Regenerate
  // control). Streams like `stream` but hits /retry with no text.
  const retry = useChatRetry();
  const upload = useUploadChatAttachment();
  // [S-509e] Deterministic review-pane actions — these hit dedicated backend
  // endpoints instead of routing a parseable text turn through the chat/LLM.
  const reviewDecide = useReviewDecide();
  const reviewRevise = useReviewRevise();
  // [chat-stop] Explicit server-side cancel of the in-flight turn (composer Stop).
  const chatCancel = useChatCancel();
  const reviewGoto = useReviewGoto();
  const reviewResolveSkips = useReviewResolveSkips();
  const qc = useQueryClient();
  // [async-review] Drive the poll from LOCAL review state, not the query's own
  // refetchInterval: the `building` pane arrives via the stream (setReview), which
  // does NOT refetch the session-detail query — so its refetchInterval would never
  // start on its own. While building, force the session-detail query to refetch
  // every 2s; each refetch re-runs findActiveByChatSession, which surfaces the
  // session (building → then the just-settled in_progress/complete/failed), and the
  // sync effect above adopts it. Stops as soon as the review leaves `building`.
  useEffect(() => {
    if (review?.status !== "building") return;
    const key = [...CHAT_KEY, "detail", sessionId, tenantId ?? "self"];
    const iv = setInterval(() => {
      void qc.invalidateQueries({ queryKey: key });
    }, 2000);
    return () => clearInterval(iv);
  }, [review?.status, sessionId, tenantId, qc]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // [S-650] Files attached since the last send — staged inline and included with
  // the next turn (with or without a typed message). Cleared once a turn goes.
  // [context-picker] What the user picked from the template / draft pickers,
  // staged for the next turn exactly like an attachment.
  const [picker, setPicker] = useState<ContextPickerKind | null>(null);
  const [pickedDrafts, setPickedDrafts] = useState<PickedRecord[]>([]);
  const [pickedTemplate, setPickedTemplate] = useState<PickedRecord | null>(null);
  const [readyAttachments, setReadyAttachments] = useState<
    Array<{ id: string; filename: string }>
  >([]);
  // [upload race] Files picked but not yet STAGED. `upload.isPending` alone is
  // half a beat too early: it flips false when the last mutation resolves,
  // while that file is staged in the .then AFTER it — a ~15ms window (measured
  // live) where Send is enabled and the final attachment isn't in
  // readyAttachments yet, so a send landing in it would silently drop that
  // file. Counted here instead: incremented before the upload starts,
  // decremented only once the file is staged (or has failed).
  const [stagingCount, setStagingCount] = useState(0);
  // [bugfix] Per-file upload failures — a single shared `upload.isError`/
  // `upload.error` (from the ONE useUploadChatAttachment() mutation hook
  // backing every file in a multi-file pick) only ever reflects the LAST
  // call to settle. Attach a scanned PDF + a DOCX and the DOCX resolving
  // after the PDF fails ERASES the PDF's error banner entirely — one pill
  // stages, no failure is shown, and the PDF is just silently absent from
  // context. Track failures explicitly, one entry per failed file.
  const [attachErrors, setAttachErrors] = useState<
    Array<{ id: string; filename: string; message: string }>
  >([]);
  const dismissAttachError = useCallback((id: string) => {
    setAttachErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);
  // [bugfix] A file that extracted SOMETHING but is suspiciously thin for
  // its size (today: a PDF with a low text-per-page density, likely mixed
  // scanned/text) still stages normally — this is a heads-up, not a
  // failure — but silently dropping that signal is the exact "succeeds
  // while quietly contributing less than it looks like" pattern this
  // session kept finding, so it's surfaced rather than discarded.
  const [attachWarnings, setAttachWarnings] = useState<
    Array<{ id: string; filename: string; message: string }>
  >([]);
  const dismissAttachWarning = useCallback((id: string) => {
    setAttachWarnings((prev) => prev.filter((w) => w.id !== id));
  }, []);

  // Stream one assistant turn into the message `asstId`, applying every
  // ChatAgentEvent to the UI. `invoke` performs the actual network stream — a
  // /messages send (sendText) or a /retry regenerate (regenerate) — so the whole
  // event-handling body (tool trace, buffered final answer, cards, stop/error
  // handling, teardown) is shared by both paths and never drifts between them.
  const consumeStream = useCallback(
    async (
      asstId: string,
      invoke: (
        onEvent: (ev: ChatStreamEvent) => void,
        signal: AbortSignal
      ) => Promise<void>
    ): Promise<void> => {
      setRunning(true);
      // The open draft is read-only while the agent works (agentDraftLock).
      setAgentTurnRunning(true);
      // [chat-stop] Fresh abort handle for THIS turn; Stop aborts it.
      const controller = new AbortController();
      abortRef.current = controller;
      // Keep a status indicator up from the moment we send until the answer
      // starts streaming, so the turn never looks blank (tool results / cards
      // arrive before the model's prose — see the tool_result branch).
      setTool("Thinking…");
      setSteps([]);
      let acc = "";
      // Once the agent calls any tool, the turn is "agentic": the model emits
      // throwaway intermediate prose between tool calls that is NOT part of the
      // saved answer. We buffer the final answer and only reveal it at the end,
      // showing the tool steps as a stacked trace meanwhile — no flicker.
      let usedTools = false;
      let finalizing = false; // final answer of an agentic turn is composing
      let refs: RecordRef[] = [];
      // Cards (tool_result) stream BEFORE the model's prose, so showing them as
      // they arrive — or even when the first token lands — makes them beat the
      // visible answer. Withhold them until the turn is SAVED (the whole message
      // has streamed), so the message renders fully first and the cards follow.
      let revealRefs = false;
      // [hardening] Same withholding as refs: generate_draft's result (and the
      // "Open in Draft Workspace" button it renders) used to attach to the
      // message the instant the tool_result arrived — well before the model's
      // buffered final text (which explains the template pick) was revealed,
      // so the button visibly appeared BEFORE its own explanation. Buffer it
      // and reveal it at "saved", alongside the text and the reference cards.
      let draftGenerated: GeneratedDraft | undefined;
      const patch = (): void =>
        setMsgs((prev) =>
          prev.map((m) =>
            m.id === asstId
              ? {
                  ...m,
                  text: acc,
                  references: revealRefs ? refs : [],
                  ...(revealRefs && draftGenerated !== undefined
                    ? { draftGenerated }
                    : {}),
                }
              : m
          )
        );

      try {
        await invoke((ev) => {
          switch (ev.type) {
            case "text_delta":
              acc += ev.text;
              if (!usedTools) {
                // Simple (no-tool) answer — stream it live as before.
                if (acc === ev.text) setTool(null);
                patch();
              } else if (!finalizing) {
                // Agentic turn: the final answer is composing. Show one status
                // line; the buffered text is revealed in full at "saved" so it
                // never flickers / gets replaced mid-stream.
                finalizing = true;
                setTool("Writing the analysis…");
              }
              break;
            case "tool_call":
              // The model is taking an action. Record it as a stacked step and
              // drop any throwaway prose streamed before it — that intermediate
              // reasoning is NOT part of the saved answer.
              usedTools = true;
              finalizing = false;
              acc = "";
              {
                // null → an internal tool with no user-facing step (see
                // chatStepLabel); the turn is still agentic, we just don't
                // narrate this particular call.
                const label = chatStepLabel(ev.tool);
                if (label !== null) setSteps((s) => [...s, label]);
              }
              setTool(null);
              patch();
              break;
            case "tool_result":
              // Cards are withheld (revealRefs=false) until the answer is ready;
              // just accumulate them. The step trace already shows progress.
              if (ev.references !== undefined && ev.references.length > 0) {
                refs = dedupeByUri([...refs, ...ev.references]);
              }
              break;
            case "elicit":
              // [S-507] The agent is asking the user to confirm/edit a small set
              // of parameters before proceeding. Attach the request to the
              // current assistant turn; AssistantMessage renders it as an inline
              // form. Clear any "Thinking…" status so the form isn't shadowed.
              setTool(null);
              setMsgs((prev) =>
                prev.map((m) =>
                  m.id === asstId
                    ? {
                        ...m,
                        elicit: {
                          tool: ev.tool,
                          prompt: ev.prompt,
                          fields: ev.fields,
                        },
                      }
                    : m
                )
              );
              break;
            case "review_step":
              // [S-509b] The clause walkthrough advanced (e.g. the agent kicked
              // off the review). Keep the latest payload as screen state; it
              // supersedes the prior one. Pane controls are now deterministic
              // (S-509e) and drive their own pending state via the mutations.
              setReview(ev);
              break;
            case "draft_generated":
              // [S-516] generate_draft produced an editable Workspace draft —
              // buffer it (like refs) so it reveals at "saved" alongside the
              // model's final text, not before it.
              draftGenerated = {
                draftId: ev.draftId,
                title: ev.title,
                url: ev.url,
                action: ev.action,
                ...(ev.status === "building" ? { status: ev.status } : {}),
                ...(ev.startedAt !== undefined ? { startedAt: ev.startedAt } : {}),
              };
              break;
            case "saved":
              // Authoritative end of turn: reveal the final answer + cards at
              // once (clean, no mid-stream replacement).
              revealRefs = true;
              // [S-802] The persisted reply wins over the raw stream: a policy
              // rewrite (an invented link stripped, a false claim corrected)
              // lands here, and it matches what a reload shows.
              if (ev.text !== undefined && ev.text.length > 0) acc = ev.text;
              if (ev.references !== undefined) {
                refs = dedupeByUri(ev.references);
              }
              patch();
              break;
            case "error":
              acc += `\n\n_[error: ${ev.message}]_`;
              patch();
              break;
            default:
              break;
          }
        }, controller.signal);
      } catch (err) {
        // [chat-stop] A user-initiated Stop aborts the fetch → AbortError. That's
        // not a failure: keep whatever streamed so far (no visible "_[stopped]_"
        // marker). The turn reads as the user message with a partial/no reply;
        // Retry lives on that user message (hover), so no footer is needed.
        if (controller.signal.aborted) {
          setMsgs((prev) =>
            prev.map((m) => (m.id === asstId ? { ...m, text: acc } : m))
          );
        } else {
          // Surface the backend's actual reason (ApiError carries it in `body`),
          // not just "API 422 <url>" — so a failure says e.g. "chat session not
          // found" instead of a bare status code.
          const detail =
            getApiErrorMessage(err) ??
            (err instanceof Error ? err.message : String(err));
          acc = `${acc}\n\n_[failed: ${detail}]_`;
          patch();
        }
      } finally {
        // Only tear down if THIS turn is still the active one (a new turn may have
        // started after a stop); guard so a late finally can't clobber it.
        if (abortRef.current === controller) abortRef.current = null;
        setRunning(false);
        setAgentTurnRunning(false);
        // A tool may have saved a version of the open draft: refetch it, so
        // the unlocked editor starts from the agent's latest, not a stale copy.
        void qc.invalidateQueries({ queryKey: DRAFTS_KEY });
        setTool(null);
        setSteps([]);
        // Title may have changed on the first turn; refresh the history list.
        void qc.invalidateQueries({ queryKey: CHAT_KEY });
      }
    },
    [qc]
  );

  // Send one user turn and stream the agent's reply. Used by the composer
  // (onNew); the attachment rides whatever message the user sends next.
  const stagedPicks = useCallback(
    (): {
      draftIds?: string[];
      templateId?: string;
      /** The same picks with titles, for the turn rendered locally before the
       *  persisted message (which carries them back) arrives. */
      records: Array<{ kind: "draft" | "template"; id: string; title: string }>;
    } => ({
      ...(pickedDrafts.length > 0
        ? { draftIds: pickedDrafts.map((d) => d.id) }
        : {}),
      ...(pickedTemplate !== null ? { templateId: pickedTemplate.id } : {}),
      records: [
        ...(pickedTemplate !== null
          ? [
              {
                kind: "template" as const,
                id: pickedTemplate.id,
                title: pickedTemplate.title,
              },
            ]
          : []),
        ...pickedDrafts.map((d) => ({
          kind: "draft" as const,
          id: d.id,
          title: d.title,
        })),
      ],
    }),
    [pickedDrafts, pickedTemplate]
  );
  const clearPicks = useCallback((): void => {
    setPickedDrafts([]);
    setPickedTemplate(null);
  }, []);

  const sendText = useCallback(
    async (
      text: string,
      attach?: Array<{ id: string; filename: string }>,
      picks?: {
        draftIds?: string[];
        templateId?: string;
        records?: Array<{ kind: "draft" | "template"; id: string; title: string }>;
      }
    ): Promise<void> => {
      const files = attach ?? [];
      const hasPicks =
        (picks?.draftIds?.length ?? 0) > 0 || (picks?.templateId ?? "").length > 0;
      // A turn needs text, a staged file, or a picked record.
      if (text.length === 0 && files.length === 0 && !hasPicks) return;

      const userId = crypto.randomUUID();
      const asstId = crypto.randomUUID();
      setMsgs((prev) => [
        ...prev,
        {
          id: userId,
          role: "user",
          text,
          createdAt: new Date().toISOString(),
          ...(files.length > 0 ? { attachments: files } : {}),
          // Shown on the turn immediately; the persisted message carries the
          // same list back on reload (ChatMessage.picked).
          ...((picks?.records?.length ?? 0) > 0 ? { picked: picks?.records } : {}),
        },
        { id: asstId, role: "assistant", text: "" },
      ]);
      await consumeStream(asstId, (onEvent, signal) =>
        stream(
          sessionId,
          text,
          tenantId,
          onEvent,
          files.map((f) => f.id),
          openRecordRef,
          signal,
          picks
        )
      );
    },
    [sessionId, tenantId, stream, consumeStream, openRecordRef]
  );

  // [chat-regenerate] Retry the LAST user turn (the hover "↻" on a user message).
  // Re-answers that turn whether the prior attempt completed, was stopped, or was
  // left in-flight — the backend drops everything after the last user turn from
  // the model request, so no marker is needed. Locally: drop everything after the
  // last user turn and stream a fresh reply in its place. Guarded against firing
  // while a turn is running.
  const retryLastUserTurn = useCallback((): void => {
    if (running) return;
    const asstId = crypto.randomUUID();
    const lastUserText = [...msgs].reverse().find((m) => m.role === "user")?.text ?? "";
    setMsgs((prev) => {
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i]!.role === "user") {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx === -1) return prev;
      return [
        ...prev.slice(0, lastUserIdx + 1),
        { id: asstId, role: "assistant", text: "" },
      ];
    });
    void consumeStream(asstId, (onEvent, signal) =>
      retry(sessionId, tenantId, onEvent, signal, lastUserText)
    );
  }, [running, msgs, sessionId, tenantId, retry, consumeStream]);

  // [chat-stop] Stop the in-flight turn (the composer's Stop button + the runtime's
  // onCancel, e.g. Escape). TWO parts: (1) abort the local stream so the UI frees
  // immediately, and (2) POST an explicit cancel so the SERVER turn stops — App
  // Hosting holds the streaming connection open until the turn completes, so the
  // socket abort alone never reaches the backend. Both best-effort.
  const onStop = useCallback(() => {
    abortRef.current?.abort();
    if (sessionId.length > 0) {
      chatCancel.mutate({
        chatSessionId: sessionId,
        ...(tenantId !== undefined ? { tenantId } : {}),
      });
    }
  }, [chatCancel, sessionId, tenantId]);

  // [S-487] If the Home welcome composer seeded an opening message, send it once
  // when the thread mounts. The ref guards against React strict-mode's double
  // effect invoke (and any re-render) so it never double-sends.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (autoSentRef.current) return;
    // Only auto-send into an EMPTY conversation. Reopening a just-created chat
    // (its first message already saved) re-mounts this with a non-empty seed —
    // sending again would duplicate the turn.
    if (seed.length > 0) return;
    const text = (initialMessage ?? "").trim();
    if (text.length === 0) return;
    autoSentRef.current = true;
    void sendText(text);
  }, [initialMessage, sendText, seed.length]);

  const onNew = useCallback(
    async (message: AppendMessage) => {
      const text = message.content
        .map((p) => (p.type === "text" ? p.text : ""))
        .join("")
        .trim();
      // Whatever's staged goes WITH this message (rendered inline on the turn).
      const files = readyAttachments;
      const picks = stagedPicks();
      setReadyAttachments([]);
      clearPicks();
      await sendText(text, files, picks);
    },
    [sendText, readyAttachments, stagedPicks, clearPicks]
  );

  // [S-650] Send with an EMPTY message: the staged file(s) go into the context
  // with no instruction. The file is shown inline on the (text-less) user turn;
  // the agent reads it and, with nothing else to go on, summarizes + asks intent.
  // Sending with a message instead responds to that message.
  const onEmptySend = useCallback(() => {
    const files = readyAttachments;
    const picks = stagedPicks();
    const hasPicks =
      (picks.draftIds?.length ?? 0) > 0 || (picks.templateId ?? "").length > 0;
    if (files.length === 0 && !hasPicks) return;
    setReadyAttachments([]);
    clearPicks();
    void sendText("", files, picks);
  }, [readyAttachments, sendText, stagedPicks, clearPicks]);

  // [S-509e] Review-pane controls now hit deterministic backend endpoints —
  // NOT the chat/LLM. Each mutation returns the next ReviewStep, which we set as
  // the pane state directly (re-attaching the `type` discriminant so it matches
  // the shape the stream handler stores). reviewSessionId is the review's own id;
  // the chat session id rides in the URL. The buttons stay disabled while any of
  // the three mutations is pending (`reviewBusy`) and re-enable on response.
  const reviewSessionId = review?.sessionId ?? null;
  const appendAssistant = useCallback((text: string): void => {
    if (text.length === 0) return;
    setMsgs((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "assistant", text },
    ]);
  }, []);
  // Apply the returned step AND flow a deterministic conversational message into
  // the chat narrating the click (acknowledge the decision + announce the next
  // clause, incl. jumping back to a skipped one). The narration is built
  // client-side from the action + the new step — the LLM is NOT in the click
  // loop (that's what made the buttons unreliable), so the buttons stay
  // deterministic while the conversation keeps flowing as the user clicks.
  const applyAndNarrate = useCallback(
    (prevReview: ReviewStepEvent | null, action: ReviewAction) =>
      (resp: { reviewStep: Omit<ReviewStepEvent, "type"> }): void => {
        setReviewActed(true);
        setReview({ type: "review_step", ...resp.reviewStep });
        // [Fix 4] Apply-as-you-go wrote the approved clause into the draft, so the
        // rail (a ["drafts", id] query with no polling) must be invalidated to pick
        // up the new version — else it keeps showing the pre-apply content. Prefix
        // invalidation refetches the draft (bumping currentVersion) AND its version
        // queries; harmless for keep/skip (the draft is unchanged).
        const draftId = resp.reviewStep.finalized?.draftId ?? resp.reviewStep.sourceDraftId;
        if (draftId != null) {
          void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, draftId] });
        }
        // [S-509h/M0b] If the backend re-read a draft edited mid-review, the plan
        // moved and the click was NOT applied — narrate the reconcile, not the
        // (non-existent) decision.
        const reconciled = resp.reviewStep.reconciled;
        if (reconciled !== undefined && reconciled !== null) {
          appendAssistant(narrateReconcile(reconciled));
          return;
        }
        // [review-compact] The stacked clause cards already record each decision
        // (a collapsed card per clause), so an ordinary click adds no chat line.
        // The skip prompt, completion, finish/review-skips and errors still narrate.
        const quietClick =
          reviewInlineEnabled() &&
          reviewCompactEnabled() &&
          (action.kind === "apply" ||
            action.kind === "keep" ||
            action.kind === "skip" ||
            action.kind === "undo") &&
          resp.reviewStep.status === "in_progress" &&
          resp.reviewStep.awaitingSkipReview !== true;
        if (prevReview !== null && !quietClick) {
          appendAssistant(
            narrateReviewAction(prevReview, resp.reviewStep, action, reviewInlineEnabled())
          );
        }
      },
    [appendAssistant, qc]
  );
  const onReviewDecide = useCallback(
    (index: number, decision: "apply" | "keep" | "skip" | "undo"): void => {
      if (reviewSessionId === null) return;
      reviewDecide.mutate(
        {
          chatSessionId: sessionId,
          reviewSessionId,
          index,
          decision,
          ...(tenantId !== undefined ? { tenantId } : {}),
        },
        { onSuccess: applyAndNarrate(review, { kind: decision, index }) }
      );
    },
    [reviewDecide, sessionId, reviewSessionId, tenantId, applyAndNarrate, review]
  );
  const onReviewGoto = useCallback(
    (index: number): void => {
      if (reviewSessionId === null) return;
      const kind = index < (review?.currentIndex ?? 0) ? "prev" : "next";
      reviewGoto.mutate(
        {
          chatSessionId: sessionId,
          reviewSessionId,
          index,
          ...(tenantId !== undefined ? { tenantId } : {}),
        },
        { onSuccess: applyAndNarrate(review, { kind, index }) }
      );
    },
    [reviewGoto, sessionId, reviewSessionId, tenantId, applyAndNarrate, review]
  );
  // [S-509f] When the walkthrough reaches the end with skipped clauses left, the
  // pane prompts instead of auto-looping. "review" walks the skips (the endpoint
  // navigates to the first one); "finish" completes the review, leaving them.
  // Same deterministic shape as decide/goto: set the returned step AND narrate.
  const onReviewResolveSkips = useCallback(
    (choice: "review" | "finish"): void => {
      if (reviewSessionId === null) return;
      reviewResolveSkips.mutate(
        {
          chatSessionId: sessionId,
          reviewSessionId,
          choice,
          ...(tenantId !== undefined ? { tenantId } : {}),
        },
        {
          onSuccess: applyAndNarrate(
            review,
            choice === "review"
              ? { kind: "resolve-review" }
              : { kind: "resolve-finish" }
          ),
        }
      );
    },
    [
      reviewResolveSkips,
      sessionId,
      reviewSessionId,
      tenantId,
      applyAndNarrate,
      review,
    ]
  );
  const reviewBusy =
    reviewDecide.isPending ||
    reviewRevise.isPending ||
    reviewGoto.isPending ||
    reviewResolveSkips.isPending;
  // [Fix 3] "Type" submits a free-form instruction from the clause card's inline
  // box. It runs the DETERMINISTIC revise (not a chat turn) so the rewrite stays
  // grounded in the certified standard: echo the tagged instruction into the
  // chat, revise this clause's recommendation, then refresh the card in place and
  // confirm. The user Approves the revised recommendation from the same card.
  const onReviewTypeSubmit = useCallback(
    (index: number, name: string, text: string): void => {
      if (reviewSessionId === null) return;
      // [clause-remove 2026-09-02] An ADDITIONAL clause has NO recommendation,
      // and the deterministic reviser below only rewrites a recommendation — so
      // every instruction typed on such a card hit `not_revisable` and died. The
      // user reported typing "remove it" and seeing nothing happen. There is
      // nothing for the reviser to do here, so hand the instruction to the AGENT
      // instead, which can act on the DRAFT (revise_draft, including its remove
      // shape). Cards that DO carry a recommendation keep the deterministic
      // path — that's what keeps an approved rewrite grounded in the standard.
      // The Type box only ever appears on the CURRENT card, so `current` is the
      // clause being typed on; the index guard keeps that assumption honest.
      const cur = review?.current;
      const hasRecommendation =
        cur != null && cur.index === index && cur.recommendation != null;
      const tagged = `[Clause ${index + 1} · ${name}] ${text}`;
      if (!hasRecommendation) {
        void sendText(tagged);
        return;
      }
      setMsgs((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "user",
          text: tagged,
        },
      ]);
      reviewRevise.mutate(
        {
          chatSessionId: sessionId,
          reviewSessionId,
          index,
          instruction: text,
          ...(tenantId !== undefined ? { tenantId } : {}),
        },
        {
          onSuccess: (resp) => {
            setReview({ type: "review_step", ...resp.reviewStep });
            appendAssistant(
              `Revised the recommendation for “${name}.” Review it above and choose Apply change if it looks right.`
            );
          },
          onError: () => {
            appendAssistant(
              `I couldn't revise “${name}” just now — please try again, or edit the clause directly in the draft.`
            );
          },
        }
      );
    },
    [
      reviewRevise,
      sessionId,
      reviewSessionId,
      tenantId,
      appendAssistant,
      review?.current,
      sendText,
    ]
  );
  // [after-review-inline] Which review UI: the inline cards-in-chat model
  // (default) or the classic side pane (localStorage `cs.reviewInline`="false").
  // The pane code stays wired either way — this only switches what renders.
  const reviewInline = reviewInlineEnabled();
  // [after-review-inline P2] Open the draft under review in the document rail so
  // it sits beside the inline clause cards. Once per distinct review draft — if
  // the user closes the rail we don't force it back (the ref guard blocks
  // re-opens even if openRecord isn't referentially stable).
  const { openRecord } = useRecordDrawer();
  const autoOpenedDraftRef = useRef<string | null>(null);
  useEffect(() => {
    if (!reviewInline || draftHost) return;
    const draftId = review?.sourceDraftId ?? null;
    if (draftId === null || draftId === autoOpenedDraftRef.current) return;
    autoOpenedDraftRef.current = draftId;
    openRecord(draftRef(draftId, "Draft under review"));
  }, [reviewInline, draftHost, review?.sourceDraftId, openRecord]);
  // [after-review-inline P2b] Publish the clause under review so the rail document
  // scrolls to + outlines it. Keyed on the current clause changing (advance,
  // reopen a receipt). Cleared when there's no clause (upload source / complete).
  useEffect(() => {
    if (!reviewInline) return;
    const draftId = review?.sourceDraftId ?? null;
    const cur = review?.current ?? null;
    if (draftId === null || cur === null) {
      setReviewFocus(null);
      return;
    }
    setReviewFocus({ draftId, name: cur.name, text: cur.draftText });
  }, [
    reviewInline,
    review?.sourceDraftId,
    review?.current?.index,
    review?.current?.name,
  ]);

  const runtime = useExternalStoreRuntime({
    messages: msgs,
    isRunning: running,
    convertMessage,
    onNew,
    // [chat-stop] Lets the runtime cancel the in-flight turn (drives the Stop
    // affordance and any assistant-ui cancel path). The runtime types onCancel as
    // async; the abort itself is synchronous, so resolve immediately.
    onCancel: () => {
      onStop();
      return Promise.resolve();
    },
  });

  const onPickFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // [bugfix] `files` was read as `[0]` only and the input lacked `multiple`,
      // so the OS picker never allowed more than one file, and even a
      // multi-select (some browsers permit it regardless) silently dropped
      // every file but the first. Upload each selected file independently —
      // each stages its OWN pill as its own upload resolves, so files that
      // finish out of order still all land instead of clobbering each other.
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;
      // Attaching STAGES the files — it does not send a turn. Each upload
      // persists on the session (so read_context_file can see it) and marks it
      // "ready"; they then ride whatever the user sends next, with or without a
      // message. (Previously the upload auto-sent a turn, which fired the agent
      // the moment you attached — the exact behavior users complained about.)
      //
      // [bugfix] Must be `mutateAsync`, not `mutate(vars, {onSuccess})`: this
      // ONE `useUploadChatAttachment()` hook instance backs every file in the
      // loop, and `mutate`'s per-call `options` (including `onSuccess`) are
      // stashed on the shared mutation OBSERVER, not the individual call —
      // calling `mutate` again before the first resolves silently overwrites
      // the first file's `onSuccess` with the second's, so only the LAST
      // file's callback ever fires (found live testing 2+ files: only the
      // last one staged a pill, even though the upload itself succeeded for
      // both). `mutateAsync` returns its own independent Promise per call,
      // unaffected by later calls on the same hook.
      setStagingCount((n) => n + files.length);
      for (const file of files) {
        void upload
          .mutateAsync({
            sessionId,
            file,
            ...(tenantId !== undefined ? { tenantId } : {}),
          })
          .then((session) => {
            // The upload returns the session; the new attachment is the most
            // recent one matching this filename. Stage its {id, filename} so
            // the send can attach it to that turn.
            const matches = session.attachments.filter(
              (a) => a.filename === file.name
            );
            const added = matches[matches.length - 1];
            if (added !== undefined) {
              setReadyAttachments((prev) =>
                prev.some((r) => r.id === added.id)
                  ? prev
                  : [...prev, { id: added.id, filename: added.filename }]
              );
            }
            if (session.attachmentWarning !== undefined) {
              setAttachWarnings((prev) => [
                ...prev,
                {
                  id: `${file.name}-${Date.now()}`,
                  filename: file.name,
                  message: session.attachmentWarning!,
                },
              ]);
            }
          })
          .catch((err: unknown) => {
            setAttachErrors((prev) => [
              ...prev,
              {
                id: `${file.name}-${Date.now()}`,
                filename: file.name,
                message:
                  getApiErrorMessage(err) ??
                  (err instanceof Error ? err.message : "upload failed"),
              },
            ]);
          })
          // Runs after the staging setState above (or after the error is
          // recorded), so Send only unlocks once this file is really accounted
          // for — never between "upload resolved" and "chip staged".
          .finally(() => setStagingCount((n) => Math.max(0, n - 1)));
      }
      e.target.value = "";
    },
    [upload, sessionId, tenantId]
  );

  const removeReadyAttachment = useCallback((id: string) => {
    setReadyAttachments((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // [chat-regenerate] Retry is offered only on the LAST user turn (re-answering an
  // earlier one would orphan everything below it). Find it so <UserMessage> can
  // show the ↻ on that message alone.
  let lastUserId: string | null = null;
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i]!.role === "user") {
      lastUserId = msgs[i]!.id;
      break;
    }
  }

  return (
    <ElicitSubmitContext.Provider value={(t, p) => void sendText(t, undefined, p)}>
    <ChatSessionIdContext.Provider value={sessionId}>
    <RetryContext.Provider
      value={{ lastUserId, busy: running, onRetry: retryLastUserTurn }}
    >
    <AssistantRuntimeProvider runtime={runtime}>
      <div
        className={
          review !== null && !reviewInline
            ? "sa-chat-shell sa-chat-shell--review"
            : "sa-chat-shell"
        }
      >
      <div className="sa-chat">
        {/* [S-687] Deliberate exception to the shared SectionHead promotion:
            this is a chat-thread title bar, not a page section — it truncates
            a dynamic, potentially-long conversation title (white-space:
            nowrap + ellipsis) inside a fixed-height bar, and sits at 14px by
            design (the smallest and most compact of the drifted sizes,
            intentionally so next to the message stream). Forcing the 18px
            canonical size would both break the truncation and visibly bloat
            a UI element that isn't playing the "section header" role. */}
        <div className="sa-chat__head">
          <span className="src-dot" />
          <h2>{title}</h2>
          <span className="meta">· {agentLabel}</span>
          {/* [S-666e] Visible + reversible, per the no-fake-UI rule — the user
              can always see and undo which persona is actually answering. */}
          {routedLabel !== null && (
            <span className="sa-route-chip" data-testid="sa-route-chip">
              Routed to {routedLabel}
              <Button
                variant="bare"
                className="sa-route-chip__back"
                onClick={() =>
                  resetActiveAgent.mutate({
                    sessionId,
                    ...(tenantId !== undefined ? { tenantId } : {}),
                  })
                }
                disabled={resetActiveAgent.isPending}
                data-testid="sa-route-chip-back"
              >
                {resetActiveAgent.isPending ? "Switching back…" : "Switch back"}
              </Button>
            </span>
          )}
        </div>
        {/* [S-650] No persistent session-wide attachment pill — files render
            inline on the user message they were sent with (see UserMessage). */}
        <ThreadPrimitive.Root className="sa-chat__root">
          {/* [hardening] Reopening a long conversation used to jump straight
              to the bottom, forcing a scroll back up to read it in order.
              Two INDEPENDENT assistant-ui mechanisms cause that, and both had
              to be disabled: scrollToBottomOnInitialize/OnThreadSwitch (an
              explicit one-shot scroll on mount) AND `autoScroll` (a resize-
              observer that pins to the bottom on ANY content resize —
              including the very first layout of a long history — whenever
              the internal isAtBottom state defaults to true, independent of
              those two props). scrollToBottomOnRunStart stays on (default)
              so an ACTIVELY STREAMING reply still auto-follows — that path
              is driven by an explicit thread.runStart scroll request, not by
              `autoScroll`, so disabling `autoScroll` doesn't affect it. */}
          <ThreadPrimitive.Viewport
            className="sa-chat__viewport"
            autoScroll={false}
            scrollToBottomOnInitialize={false}
            scrollToBottomOnThreadSwitch={false}
          >
            <ThreadPrimitive.Empty>
              <div className="sa__placeholder">
                Ask a question to get started. The agent can search the certified
                library and your precedents.
              </div>
            </ThreadPrimitive.Empty>
            <ThreadPrimitive.Messages
              components={{ UserMessage, AssistantMessage }}
            />
            {/* [after-review-inline] Inline model: the whole review renders as
                receipts + one open card in the chat stream (its own completion
                summary replaces the ghost card). */}
            {review !== null && reviewInline && (
              <InlineReview
                review={review}
                pending={reviewBusy}
                onDecide={onReviewDecide}
                onGoto={onReviewGoto}
                onTypeSubmit={onReviewTypeSubmit}
                revising={reviewRevise.isPending}
                revealNext={reviewActed}
                onResolveSkips={onReviewResolveSkips}
                {...(review.current !== null && review.sourceDraftId != null
                  ? {
                      // [Fix #4] "Show in draft" reveals + scrolls the rail draft
                      // to the clause under review.
                      onJumpToDraft: (): void => {
                        const draftId = review.sourceDraftId as string;
                        const cur = review.current;
                        if (cur === null) return;
                        if (!draftHost) openRecord(draftRef(draftId, "Draft under review"));
                        scrollToClauseNow({
                          draftId,
                          name: cur.name,
                          text: cur.draftText,
                        });
                      },
                    }
                  : {})}
              />
            )}
            {review !== null &&
              review.status === "complete" &&
              !reviewInline && (
                <ReviewGhostCard review={review} />
              )}
            {/* [thinking] ONE animated status line while a turn runs and the
                visible answer hasn't started (agentic turns buffer their final
                text). It shows the latest step — REPLACING the previous, not
                stacking — with a spinner so the user always sees the system is
                working. No separate empty bubble, no growing trace. */}
            {running &&
              (() => {
                const lastA = [...msgs].reverse().find((m) => m.role === "assistant");
                if ((lastA?.text ?? "").length > 0) return null; // answer is streaming
                const label =
                  tool ?? (steps.length > 0 ? steps[steps.length - 1]! : "Thinking…");
                return (
                  <div
                    className="sa-chat__thinking"
                    role="status"
                    aria-live="polite"
                    data-testid="chat-thinking"
                  >
                    <span className="sa-chat__spinner" aria-hidden="true" />
                    <span className="sa-chat__thinking-label">{label}</span>
                  </div>
                );
              })()}
          </ThreadPrimitive.Viewport>

          <div className="sa-chat__footer">
            <Button
              variant="bare"
              className="sa-chat__attach"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              title="Attach a context file"
            >
              {upload.isPending ? "Uploading…" : "📎 Attach"}
            </Button>
            {/* [context-picker] A file is not the only kind of context. A draft
                already in the Workspace, or the certified template to build on,
                had no affordance at all — you had to remember the exact title
                and hope the agent resolved it. */}
            <Button
              variant="bare"
              className="sa-chat__attach"
              onClick={() => setPicker("template")}
              title="Add a certified template"
              aria-label="Add a certified template"
            >
              📐 Template
            </Button>
            <Button
              variant="bare"
              className="sa-chat__attach"
              onClick={() => setPicker("draft")}
              title="Add a draft from the Workspace"
              aria-label="Add a draft from the Workspace"
            >
              📄 Draft
            </Button>
            <ContextPicker
              kind={picker}
              onClose={() => setPicker(null)}
              onSelect={(kind, record) => {
                if (kind === "template") {
                  setPickedTemplate(record);
                  return;
                }
                setPickedDrafts((prev) =>
                  prev.some((d) => d.id === record.id) ? prev : [...prev, record]
                );
              }}
            />
            {attachErrors.map((e) => (
              <span
                key={e.id}
                role="alert"
                className="sa-chat__attach-err"
                style={{ color: "var(--cs-risk-high-text)" }}
              >
                Couldn't attach {e.filename}: {e.message}
                <Button
                  variant="bare"
                  className="sa-chat__attach-err-x"
                  onClick={() => dismissAttachError(e.id)}
                  aria-label={`Dismiss error for ${e.filename}`}
                >
                  ×
                </Button>
              </span>
            ))}
            {attachWarnings.map((w) => (
              <span
                key={w.id}
                role="status"
                className="sa-chat__attach-err sa-chat__attach-warn"
                data-testid={`attach-warning-${w.id}`}
              >
                {w.filename}: {w.message}
                <Button
                  variant="bare"
                  className="sa-chat__attach-err-x"
                  onClick={() => dismissAttachWarning(w.id)}
                  aria-label={`Dismiss warning for ${w.filename}`}
                >
                  ×
                </Button>
              </span>
            ))}
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".docx,.pdf,.txt,.md,.markdown,.html,.htm,.eml,.vtt,.srt"
              className="sa-chat__file"
              onChange={onPickFile}
              data-testid="sa-chat-file-input"
            />
            {/* [context-picker] Picked records stage in the SAME block as
                uploaded files, above the composer. They used to sit inline in
                the button row, which squeezed the text box as chips were added
                — reported live. */}
            {(readyAttachments.length > 0 ||
              pickedDrafts.length > 0 ||
              pickedTemplate !== null) && (
              <div className="sa-chat__ready" role="status" data-testid="attach-ready">
                <span className="sa-chat__ready-txt">
                  Staged — send with a message, or send empty to just add
                  {readyAttachments.length + pickedDrafts.length === 1 &&
                  pickedTemplate === null
                    ? " it"
                    : " them"}{" "}
                  to the conversation:
                </span>
                <div className="sa-chat__ready-chips">
                  {pickedTemplate !== null && (
                    <span
                      className="sa-chip sa-chip--removable"
                      title={pickedTemplate.title}
                    >
                      📐 {pickedTemplate.title}
                      <Button
                        variant="bare"
                        className="sa-chip__x"
                        onClick={() => setPickedTemplate(null)}
                        aria-label={`Remove template ${pickedTemplate.title}`}
                      >
                        ×
                      </Button>
                    </span>
                  )}
                  {pickedDrafts.map((d) => (
                    <span key={d.id} className="sa-chip sa-chip--removable" title={d.title}>
                      📄 {d.title}
                      <Button
                        variant="bare"
                        className="sa-chip__x"
                        onClick={() =>
                          setPickedDrafts((prev) => prev.filter((x) => x.id !== d.id))
                        }
                        aria-label={`Remove draft ${d.title}`}
                      >
                        ×
                      </Button>
                    </span>
                  ))}
                </div>
                <div className="sa-chat__ready-chips">
                  {readyAttachments.map((a) => (
                    <span key={a.id} className="sa-chip sa-chip--removable" title={a.filename}>
                      📎 {a.filename}
                      <Button
                        variant="bare"
                        className="sa-chip__x"
                        onClick={() => removeReadyAttachment(a.id)}
                        aria-label={`Remove ${a.filename}`}
                        data-testid={`attach-remove-${a.id}`}
                      >
                        ×
                      </Button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <ComposerPrimitive.Root className="sa-composer">
              <ComposerPrimitive.Input
                className="sa-composer__input"
                placeholder="Ask the agent…"
              />
              {/* [S-650] Custom Send so an empty message still sends when a file
                  is staged (the built-in ComposerPrimitive.Send disables on
                  empty). Text present → normal send; empty + staged file →
                  attach-only turn. */}
              <ComposerSend
                // [context-picker] Staged PICKS count toward the empty-send the
                // same as uploaded files: with only a template picked, Send was
                // dead until you typed something, while an attached file could be
                // sent on its own. Attach / Template / Draft all behave alike.
                readyCount={
                  readyAttachments.length +
                  pickedDrafts.length +
                  (pickedTemplate !== null ? 1 : 0)
                }
                running={running}
                uploading={upload.isPending || stagingCount > 0}
                onEmptySend={onEmptySend}
                onStop={onStop}
              />
            </ComposerPrimitive.Root>
          </div>
        </ThreadPrimitive.Root>
      </div>
      {/* [after-review-inline] Classic side pane — kept, shown only when the
          inline model is switched off (localStorage `cs.reviewInline`="false"). */}
      {review !== null && !reviewInline && (
        <ReviewPane
          review={review}
          pending={reviewBusy}
          onDecide={onReviewDecide}
          onGoto={onReviewGoto}
          onResolveSkips={onReviewResolveSkips}
          onTypeSubmit={onReviewTypeSubmit}
          revising={reviewRevise.isPending}
        />
      )}
      </div>
    </AssistantRuntimeProvider>
    </RetryContext.Provider>
    </ChatSessionIdContext.Provider>
    </ElicitSubmitContext.Provider>
  );
}

// [rail epic] Builds a "draft" RecordRef from just an id + title — the
// generate_draft/finalize-review response never carries a tenantId, but
// RecordDrawer's draft dispatch (DraftDetail embedded) never reads it
// (server-side auth resolves the draft), so `null` is correct here, not a
// shortcut. Shared by MessageDraftCard and ReviewGhostCard below.
function draftRef(id: string, title: string): RecordRef {
  return {
    type: "draft",
    id,
    tenantId: null,
    title,
    uri: `csai://draft/global/${id}`,
  };
}

// [S-509b/c/e] Ghost reviewed-draft card — appears in the chat once the
// walkthrough completes. Creating a Workspace draft is an EXPLICIT, opt-in last
// step (S-509c): the review lives in the conversation by default. So while no
// draft has been created (draftId null) the card offers a "Create draft in
// Workspace" button (which hits the deterministic create-draft endpoint, S-509e);
// once the user creates one, its "Open draft" opens it in the SAME
// drawer/rail every other record click does (openRecord) — resolving to
// Home's rail or DraftDetail's overlay, whichever is mounted — instead of a
// hard navigation that abandons the conversation. "Open in new tab ↗" inside
// that drawer/rail is the escape hatch for the full Draft Workspace route.
function ReviewGhostCard({
  review,
}: {
  review: ReviewStepEvent;
}): JSX.Element {
  const draftId = review.finalized?.draftId ?? review.sourceDraftId ?? null;
  const applied = review.finalized?.applied ?? review.counts.applied;
  const kept = review.finalized?.kept ?? review.counts.kept;
  const { openRecord } = useRecordDrawer();
  return (
    <div className="sa-ghost" aria-label="Reviewed draft">
      <div className="sa-ghost__doclink">
        <span className="sa-ghost__icon" aria-hidden="true">
          📄
        </span>
        <span className="sa-ghost__text">
          <span className="sa-ghost__title">Reviewed draft</span>
          <span className="sa-ghost__sub">
            {applied} applied · {kept} kept
            {/* [Fix 4] Approved clauses are written to the draft as-you-go; a
                draft always exists by completion (existing draft, or one
                materialized for an upload). No "create draft" step. */}
            {draftId === null
              ? " · saved in this conversation"
              : " · applied to your draft"}
          </span>
        </span>
        {draftId !== null && (
          <Button
            variant="bare"
            className="sa-ghost__open"
            onClick={() => openRecord(draftRef(draftId, "Reviewed draft"))}
          >
            Open draft →
          </Button>
        )}
      </div>
    </div>
  );
}

// [S-509] Deterministic, conversational narration for a review-pane click. The
// pane actions are deterministic (they bypass the LLM for reliability), but the
// conversation should still FLOW as the user clicks apply / keep / skip / back /
// next — so we build a short agent message from the action + the resulting step,
// acknowledging the decision and announcing the next clause (incl. jumping back
// to a clause that was skipped earlier). No LLM in this loop.
function trimReco(text: string, max = 180): string {
  const t = text.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
// [S-509f] A clause action (apply/keep/skip + prev/next nav) carries the index it
// acted on; the end-of-walkthrough skip-resolution actions (review/finish) don't.
type ReviewAction =
  | { kind: "apply" | "keep" | "skip" | "undo" | "prev" | "next"; index: number }
  | { kind: "resolve-review" }
  | { kind: "resolve-finish" };

// Full per-clause briefing for the chat — mirrors the agent's first-clause message
// (draft text + certified standard + why + the Apply/Keep/Skip prompt) so EVERY
// clause gets the same explanation as the user walks the review, built
// deterministically from the review step (no LLM in the click loop).
function clauseBriefing(cur: ReviewStepCurrent): string {
  const parts = [`**Clause ${cur.index + 1}: ${cur.name}**`];
  // draftText is authored HTML — convert to readable text so the narration never
  // shows raw "<h2>…</h2><p>…</p>" markup (same treatment the cards use).
  parts.push(`Your draft: "${trimReco(htmlToText(cur.draftText), 600)}"`);
  if (cur.recommendation?.text != null && cur.recommendation.text.length > 0) {
    parts.push(`Certified standard: "${trimReco(cur.recommendation.text, 600)}"`);
    if (cur.recommendation.why != null && cur.recommendation.why.length > 0) {
      parts.push(cur.recommendation.why);
    }
    // [S-509g] Surface the L2 precedent (and the agent's take) alongside the L1
    // certified standard, so the chat narration matches the review pane — the
    // user sees what the standard says AND what comparable past deals did.
    const precedent = cur.recommendation.precedent;
    if (precedent != null) {
      const titles = precedent.comparables
        .map((c) => `${c.title} (${c.term})`)
        .join("; ");
      parts.push(
        titles.length > 0
          ? `**Precedent:** ${precedent.summary} — ${titles}`
          : `**Precedent:** ${precedent.summary}`
      );
    }
    if (cur.recommendation.take != null && cur.recommendation.take.length > 0) {
      parts.push(`**Worth noting:** ${cur.recommendation.take}`);
    }
    parts.push("Apply the certified language, keep the original, or skip this clause for now.");
  } else {
    parts.push(
      "This clause matches the certified standard — nothing to change. Keep it or skip."
    );
  }
  return parts.join("\n\n");
}
// [S-509h/M0b] Deterministic narration when a decide re-read a draft edited
// mid-review. The client never applied the click (the plan moved under it), so
// this replaces the usual decision narration and tells the user to re-check.
function narrateReconcile(r: {
  modified: number;
  added: number;
  deleted: number;
}): string {
  const parts: string[] = [];
  if (r.modified > 0) {
    parts.push(`${r.modified} clause${r.modified === 1 ? "" : "s"} changed`);
  }
  if (r.added > 0) parts.push(`${r.added} new`);
  if (r.deleted > 0) parts.push(`${r.deleted} removed`);
  const what = parts.length > 0 ? ` (${parts.join(", ")})` : "";
  return `The draft changed since we started this review, so I re-read it and refreshed the analysis${what}. Please re-check the highlighted clauses — I didn't apply that last action against the older text.`;
}

// Where a finished review's result already is. Since review-v2 Fix 4 an Apply
// is written into the draft as it is made (an upload's review is saved as a new
// draft on completion), so there is nothing left to save. This line used to ASK
// "Want me to create an editable draft in the Workspace, or keep this in the
// conversation?" — live 2026-09-27 the "yes" was routed to drafting, which built
// a second, fresh draft from the template.
function reviewOutcome(next: Omit<ReviewStepEvent, "type">): string {
  const saved = next.finalized?.draftId ?? null;
  if (saved === null) {
    // The write is best-effort server-side: applied changes with no draft means it did not land.
    return next.counts.applied > 0
      ? "The applied changes couldn't be written to a draft."
      : "Nothing was applied, so nothing was written to a draft.";
  }
  return saved === next.sourceDraftId
    ? "The applied changes are already in your draft."
    : "The reviewed draft is saved in your Workspace.";
}

function narrateReviewAction(
  prev: ReviewStepEvent,
  next: Omit<ReviewStepEvent, "type">,
  action: ReviewAction,
  // [inline] In the inline model every clause renders as its OWN card in the
  // stream, so re-narrating the next clause's full briefing here is a redundant
  // (and HTML-y) duplicate — narrate only the decision receipt. The classic side
  // pane has no per-clause card in the chat, so it still gets the full briefing.
  inline: boolean
): string {
  // [S-509f] End-of-walkthrough skip resolution — no clause was acted on.
  if (action.kind === "resolve-finish") {
    return `Finished the review — ${next.counts.applied} applied, ${next.counts.kept} kept, ${next.counts.skipped} left as-is.`;
  }
  if (action.kind === "resolve-review") {
    const n = next.counts.skipped;
    const word = `clause${n === 1 ? "" : "s"} you left to decide later`;
    const cur = next.current;
    if (cur === null || inline) return `Let's go back through the ${n} ${word}.`;
    return `Let's go back through the ${n} ${word}, starting here:\n\n${clauseBriefing(cur)}`;
  }
  const actedName = prev.steps[action.index]?.name ?? `clause ${action.index + 1}`;
  const actedNum = action.index + 1;
  // [review-honest] Narrate what happened to the draft, not which button was
  // pressed: an Apply change that was held back, or an Undo that could not
  // revert, left the clause as it was — the card carries the reason.
  // The backend sets decisionNote exactly when the decision did not land, and
  // parks on that clause.
  const missed = next.current !== null && next.current.index === action.index ? (next.current.decisionNote ?? "") : "";
  if ((action.kind === "apply" || action.kind === "undo") && missed.length > 0) {
    return `Clause ${actedNum} — ${actedName} was not changed. ${missed}`;
  }
  const lead =
    action.kind === "apply"
      ? `Applied the change to clause ${actedNum} — ${actedName}.`
      : action.kind === "keep"
        ? `Kept the original on clause ${actedNum} — ${actedName}.`
        : action.kind === "skip"
          ? `Left clause ${actedNum} — ${actedName} to decide later; we'll come back to it.`
          : action.kind === "undo"
            ? `Undid the change on clause ${actedNum} — ${actedName}; the original text is back and it's yours to decide again.`
            : "";
  // [S-509f] This decision just exhausted the pending clauses but skips remain —
  // prompt the user to walk them or finish, rather than narrating a next clause.
  if (next.awaitingSkipReview === true) {
    return `${lead}${lead.length > 0 ? " " : ""}You've gone through all the clauses — you left ${next.counts.skipped} to decide later. Want to review them now, or finish the review?`.trim();
  }
  if (next.status === "complete") {
    return `${lead}${lead.length > 0 ? " " : ""}That's every clause reviewed — ${next.counts.applied} applied, ${next.counts.kept} kept. ${reviewOutcome(next)}`.trim();
  }
  const cur = next.current;
  if (cur === null) return lead.trim();
  // Every clause gets the SAME full briefing as the user moves to it (this was
  // the regression: only clause 1, narrated by the agent, had the breakdown).
  const revisitingSkip = next.steps[cur.index]?.decision === "skipped";
  const skipNote = revisitingSkip ? "Back to a clause you skipped earlier." : "";
  // Inline mode shows the next clause as its own card — omit the redundant briefing.
  const briefing = inline ? "" : clauseBriefing(cur);
  return [lead, skipNote, briefing]
    .filter((s) => s.length > 0)
    .join("\n\n")
    .trim();
}

// Friendly, human-readable label for each agent tool action — shown in the
// stacked progress trace while a turn runs. `null` hides the step entirely:
// some tools are internal bookkeeping the agent is explicitly told never to
// narrate (record_draft_intake) or produce their own visible UI (the elicit
// form, the routing chip), so a robotic "Using record draft intake…" line is
// noise at best and a leak of plumbing at worst.
//
// [bugfix] The default branch used to catch the SLOWEST tools in the product.
// A generate_draft turn is ~30–40s of model time, and because an agentic turn
// buffers all prose until it's saved, that trace line is the ONLY thing on
// screen for the whole wait — live-observed as "nothing happened", with the
// user re-sending "proceed" mid-generation. Name the long-running work.
function chatStepLabel(tool: string): string | null {
  switch (tool) {
    case "read_context_file":
      return "Reading the attached document…";
    case "search_certified_library":
      return "Checking the certified standard…";
    case "get_template_text":
      return "Reading the standard's text…";
    case "search_precedents":
    case "filter_precedents":
      return "Reviewing precedents…";
    case "get_precedent_text":
      return "Reading a precedent…";
    case "aggregate_precedent_measures":
      return "Crunching the precedent numbers…";
    case "list_measures":
      return "Checking which measures are tracked…";
    case "compare_clause_terms":
      return "Comparing clause terms…";
    // Drafting — the slow ones. These carry a whole model round-trip that
    // writes a full document, so the label says what's being built.
    case "generate_draft":
      return "Writing the draft from the certified template…";
    case "update_draft":
      return "Rewriting the draft…";
    case "edit_draft_section":
      return "Editing the draft…";
    case "find_draft_gaps":
      return "Scanning the draft for open placeholders…";
    case "list_my_drafts":
      return "Looking up your drafts…";
    case "get_draft_text":
      return "Reading the draft…";
    case "match_context_to_draft":
      return "Matching the new context to the draft…";
    // Review walkthrough.
    case "review_against_standard":
      return "Reviewing against the certified standard…";
    case "start_clause_review":
      return "Lining the clauses up against the standard…";
    case "record_clause_decision":
      return "Recording the decision…";
    case "finalize_clause_review":
    case "create_draft_from_review":
      return "Saving the reviewed draft…";
    // Long-form expansion.
    case "longform_start":
      return "Opening the long-form workflow…";
    case "longform_select_template":
      return "Choosing the long-form template…";
    case "longform_add_context":
      return "Folding in the added document…";
    case "longform_generate":
      return "Building the long-form draft…";
    // Plumbing with its own UI (or none) — never shown as a step.
    case "record_draft_intake":
    case "request_parameters":
    case "route_to_agent":
      return null;
    default:
      return `Using ${tool.replace(/_/g, " ")}…`;
  }
}

function convertMessage(m: UIMsg): ThreadMessageLike {
  return {
    role: m.role,
    content: [{ type: "text", text: m.text }],
    // [S-413] Carry the turn's record refs as message metadata so
    // <MessageReferences> can render them as openable cards.
    // [S-507] …and any pending confirmation request for <MessageElicitForm>.
    metadata: {
      custom: {
        // [chat-regenerate] Our own UIMsg id + createdAt, so UserMessage (deep in
        // assistant-ui's loop, which assigns its OWN message ids) can match the
        // last user turn (to show Retry) and render the hover timestamp.
        id: m.id,
        ...(m.createdAt !== undefined ? { createdAt: m.createdAt } : {}),
        references: m.references ?? [],
        ...(m.attachments !== undefined ? { attachments: m.attachments } : {}),
        ...(m.picked !== undefined ? { picked: m.picked } : {}),
        ...(m.elicit !== undefined ? { elicit: m.elicit } : {}),
        ...(m.draftGenerated !== undefined
          ? { draftGenerated: m.draftGenerated }
          : {}),
      },
    },
  };
}

function UserMessage(): JSX.Element {
  return (
    <MessagePrimitive.Root className="sa-msg sa-msg--user">
      <div className="sa-msg__bubble">
        <MessageAttachments />
        <MessagePicked />
        <MessagePrimitive.Parts />
      </div>
      <UserMessageActions />
    </MessagePrimitive.Root>
  );
}

// [chat-regenerate] Hover row under a user message — timestamp · copy · retry
// (the Claude pattern). Revealed on hover of the message (CSS). Copy is available
// on every user turn; Retry only on the LAST one (re-answering an earlier turn
// would orphan everything below it). Retry re-answers that turn in place.
function UserMessageActions(): JSX.Element | null {
  const custom = useMessage(
    (m) => m.metadata?.custom as { id?: string; createdAt?: string } | undefined
  );
  const text = useMessage((m) =>
    m.content.map((p) => (p.type === "text" ? p.text : "")).join("")
  );
  const { lastUserId, busy, onRetry } = useRetry();
  const [copied, setCopied] = useState(false);
  const id = typeof custom?.id === "string" ? custom.id : null;
  const isLast = id !== null && id === lastUserId;
  const onCopy = (): void => {
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {}
    );
  };
  return (
    <div className="sa-msg__actions" data-testid="user-msg-actions">
      {custom?.createdAt !== undefined && (
        <span className="sa-msg__time" title={new Date(custom.createdAt).toLocaleString()}>
          {formatChatTime(custom.createdAt)}
        </span>
      )}
      <Button
        variant="bare"
        className="sa-msg__action"
        title="Copy message"
        aria-label="Copy message"
        data-testid="user-msg-copy"
        onClick={onCopy}
      >
        {copied ? "Copied" : "⧉ Copy"}
      </Button>
      {isLast && !busy && (
        <Button
          variant="bare"
          className="sa-msg__action"
          title="Retry — re-answer this message"
          aria-label="Retry this message"
          data-testid="user-msg-retry"
          onClick={onRetry}
        >
          ↻ Retry
        </Button>
      )}
    </div>
  );
}

// A compact, human timestamp for the hover row: "just now" / "5m ago" / "3h ago"
// / "6 days ago", falling back to a locale date for anything older than a week.
function formatChatTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

// [S-650] Files sent WITH this turn — rendered inline as chips on the user
// message (claude.ai style), instead of a persistent session-wide pill. Reads
// the attachments off the message metadata (see convertMessage).
// [rail epic] Each chip opens the SAME attachment preview a draft citation
// does (RecordDrawer's "attachment" case) — whatever format the file was
// (PDF/DOCX/EML/plain text/…), that preview already renders the extracted
// text the upload pipeline produced, not the raw bytes, so it's format-
// agnostic by construction rather than something this needs to special-case.
function MessageAttachments(): JSX.Element | null {
  const sessionId = useChatSessionId();
  const { openRecord } = useRecordDrawer();
  const attachments = useMessage(
    (m) =>
      (m.metadata?.custom as { attachments?: Array<{ id: string; filename: string }> })
        ?.attachments
  );
  if (attachments === undefined || attachments.length === 0) return null;
  return (
    <div className="sa-msg__attachments" data-testid="msg-attachments">
      {attachments.map((a) => (
        <Button
          key={a.id}
          variant="bare"
          className="sa-chip"
          title={a.filename}
          onClick={() => openRecord(attachmentRef(sessionId, a))}
        >
          📎 {a.filename}
        </Button>
      ))}
    </div>
  );
}

// [context-picker] Records attached from the template / draft pickers, rendered
// on the turn exactly as an uploaded file is. Reported live: the chip vanished
// the instant Send was pressed, which reads as the pick having been dropped —
// and since the platform already holds the document, it should look no
// different from a file you attached. A draft chip opens the draft in the
// record drawer, the same as clicking it anywhere else.
function MessagePicked(): JSX.Element | null {
  const { openRecord } = useRecordDrawer();
  const picked = useMessage(
    (m) =>
      (
        m.metadata?.custom as {
          picked?: Array<{ kind: "draft" | "template"; id: string; title: string }>;
        }
      )?.picked
  );
  if (picked === undefined || picked.length === 0) return null;
  return (
    <div className="sa-msg__attachments" data-testid="msg-picked">
      {picked.map((r) => (
        <Button
          key={`${r.kind}:${r.id}`}
          variant="bare"
          className="sa-chip"
          title={r.title}
          onClick={() =>
            openRecord({
              type: r.kind,
              id: r.id,
              tenantId: null,
              title: r.title,
              uri: `csai://${r.kind}/${r.id}`,
            })
          }
        >
          {r.kind === "template" ? "📐" : "📄"} {r.title}
        </Button>
      ))}
    </div>
  );
}

// Builds an "attachment" RecordRef from the {id, filename} the message
// carries. `id` here is the bare attachment id; RecordRef's composite id
// convention for attachment/chat_message ({chatSessionId}:{attachmentId},
// see RecordRef.ts's doc comment) is assembled here rather than on the
// server since this ref never leaves the client. `tenantId: null` is
// correct, not a shortcut — RecordDrawer's attachment dispatch never reads
// it (attachments are session-scoped, not layer-scoped), mirroring
// precedentRef's inline-construction pattern in DraftDetail.tsx (importing
// makeRecordRef as a VALUE from @csai/core would pull the core barrel into
// the browser bundle).
function attachmentRef(
  sessionId: string,
  a: { id: string; filename: string }
): RecordRef {
  const compositeId = `${sessionId}:${a.id}`;
  return {
    type: "attachment",
    id: compositeId,
    tenantId: null,
    title: a.filename,
    uri: `csai://attachment/global/${compositeId}`,
  };
}

function AssistantMessage(): JSX.Element {
  // [thinking] While a turn is in flight the assistant message text is empty
  // (agentic turns buffer their final answer). Don't render a blank bubble — the
  // animated status line below the thread is the sole "working" indicator. The
  // elicit form / draft card / references still render, since they can accompany
  // an empty-text turn.
  const hasText = useMessage((m) =>
    m.content.some((p) => p.type === "text" && p.text.trim().length > 0)
  );
  return (
    <MessagePrimitive.Root className="sa-msg sa-msg--assistant">
      {hasText && (
        <div className="sa-msg__bubble sa-md">
          <MessagePrimitive.Parts components={{ Text: MarkdownText }} />
        </div>
      )}
      <MessageElicitForm />
      <MessageDraftCard />
      <MessageReferences />
    </MessagePrimitive.Root>
  );
}

// [rail epic] When generate_draft attaches a draft to the turn, render an
// "Open draft" card beneath the bubble — a real button, not a raw URL in
// prose. Opens in the SAME drawer/rail every other record click does
// (openRecord), same reasoning as ReviewGhostCard above: a freshly
// generated draft is exactly the kind of thing you want to glance at
// without losing the conversation that just built it. "Open in new tab ↗"
// inside that drawer/rail is the escape hatch to the full Draft Workspace
// route (draft.url) for anyone who wants the full editor.
/** A card is a glance, not a report: the rest are in the draft's own record. */
const MAX_SHOWN_GAPS = 3;

function MessageDraftCard(): JSX.Element | null {
  const { openRecord } = useRecordDrawer();
  const draft = useMessage(
    (m) =>
      (m.metadata?.custom as { draftGenerated?: GeneratedDraft } | undefined)
        ?.draftGenerated
  );
  // [S-798] A chat-started generation lands after the reply: poll the draft
  // while the marker says `building`, then show the landed title (or the
  // honest failure). Ordinary cards never fetch.
  //
  // [S-802] A card reports the generation IT announced, and stops there. Every
  // card for a draft points at the same id, so when a retry sets that draft
  // building again the earlier cards were following it too — a failed draft
  // from ten minutes ago started spinning a second time, next to the card that
  // was actually running. So once this card's generation reaches a terminal
  // state it keeps that, and polling stops with it.
  const fetched = useDraft(draft?.status === "building" ? draft.draftId : undefined).data;
  if (draft === undefined) return null;
  // The draft we fetched carries whatever generation is running NOW; it is this
  // card's only when it is the one this card announced.
  const { build, superseded, mine } = draftCardState(draft, fetched?.build);
  const shown = mine ? fetched : undefined;
  const title = build === "complete" && shown !== undefined ? shown.title : draft.title;
  // Terms from the deal the certified template had no place for: listed here so
  // nothing disappears silently; the contract itself is left as drafted.
  const unplaced = build === "complete" ? (shown?.build?.unplacedTerms ?? []) : [];
  // Optional wording from the certified template that the deal did not call for:
  // removed from the draft, and said so here so the lawyer can put it back.
  const optional = build === "complete" ? shown?.build?.optionalWording : undefined;
  // A draft may be incomplete: say what was done and what is left.
  const left = build === "complete" ? shown?.build?.whatsLeft : undefined;
  const progress = build === "building" ? shown?.build?.progress : undefined;
  const barPct =
    progress?.done !== undefined && progress.total !== undefined
      ? Math.round((progress.done / progress.total) * 100)
      : undefined;
  return (
    <div
      className="sa-ghost"
      aria-label={draft.action === "updated" ? "Updated draft" : "Generated draft"}
    >
      <div className="sa-ghost__doclink">
        <span className="sa-ghost__icon" aria-hidden={build !== "building"}>
          {build === "building" ? <Spinner size={16} label="Generating the draft" /> : "📄"}
        </span>
        <span className="sa-ghost__text">
          <span className="sa-ghost__title">{title}</span>
          <span className="sa-ghost__sub" aria-live="polite">
            {build === "building"
              ? <BuildStatus progress={progress} startedAt={shown?.build?.startedAt} />
              : build === "failed"
                ? `Draft generation failed${shown?.build?.error ? ` — ${shown.build.error}` : ""}`
                : superseded
                  ? "Replaced by a newer generation below"
                  : draft.action === "updated"
                    ? "Draft updated — a new version was saved"
                    : "Draft generated from the certified template"}
          </span>
        </span>
        <Button
          variant="bare"
          className="sa-ghost__open"
          disabled={build !== "complete"}
          onClick={() => openRecord(draftRef(draft.draftId, title))}
        >
          Open draft →
        </Button>
      </div>
      {barPct !== undefined && (
        <div
          className="sa-ghost__bar"
          role="progressbar"
          aria-label="Draft generation progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={barPct}
        >
          <span className="sa-ghost__bar-fill" style={{ width: `${barPct}%` }} />
        </div>
      )}
      {left !== undefined && (
        <p className="sa-ghost__gaps" data-testid="draft-whats-left">
          {whatsLeftText(left)}
        </p>
      )}
      {unplaced.length > 0 && (
        <p className="sa-ghost__gaps" data-testid="draft-unplaced-terms">
          Not found in this draft:{" "}
          {unplaced
            .slice(0, MAX_SHOWN_GAPS)
            .map((t) => `${t.name} — ${t.value}`)
            .join(" · ")}
          {unplaced.length > MAX_SHOWN_GAPS ? ` · +${unplaced.length - MAX_SHOWN_GAPS} more` : ""}
        </p>
      )}
      {optional !== undefined && optional.omitted.length > 0 && (
        <p className="sa-ghost__gaps" data-testid="draft-omitted-wording" title={optional.omitted.join("\n")}>
          Left out, not in the deal: {optional.omitted.slice(0, MAX_SHOWN_GAPS).join(" · ")}
          {optional.omitted.length > MAX_SHOWN_GAPS ? ` · +${optional.omitted.length - MAX_SHOWN_GAPS} more` : ""}
        </p>
      )}
      {optional !== undefined && optional.unconfirmed.length > 0 && (
        <p className="sa-ghost__gaps" data-testid="draft-unconfirmed-wording" title={optional.unconfirmed.join("\n")}>
          Optional wording to check, left in brackets: {optional.unconfirmed.slice(0, MAX_SHOWN_GAPS).join(" · ")}
          {optional.unconfirmed.length > MAX_SHOWN_GAPS ? ` · +${optional.unconfirmed.length - MAX_SHOWN_GAPS} more` : ""}
        </p>
      )}
    </div>
  );
}

/** "Filled 41 of 97 variables · 56 left to fill · 10 drafter notes to review". */
function whatsLeftText(w: NonNullable<NonNullable<Draft["build"]>["whatsLeft"]>): string {
  const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
  const parts: string[] = [];
  if (w.variablesFilled !== null && w.variables !== null) parts.push(`Filled ${w.variablesFilled} of ${plural(w.variables, "variable", "variables")}`);
  parts.push(
    w.variablesLeft === 0
      ? "No variables left to fill"
      : parts.length > 0
        ? `${w.variablesLeft} left to fill`
        : `${plural(w.variablesLeft, "variable", "variables")} left to fill`
  );
  if (w.notesLeft > 0) parts.push(`${plural(w.notesLeft, "drafter note", "drafter notes")} to review`);
  return parts.join(" · ");
}

type BuildProgress = NonNullable<NonNullable<Draft["build"]>["progress"]>;

/** What a running generation is doing, in words, plus how long it has run. */
function buildStageText(progress: BuildProgress | undefined): string {
  if (progress === undefined) return "Starting — reading the template and your deal";
  const parts = progress.done !== undefined && progress.total !== undefined ? ` — ${progress.done} of ${progress.total} parts` : "";
  switch (progress.stage) {
    case "writing":
      return `Writing the draft${parts}`;
    case "correcting":
      return `Second pass — fixing what the quality check caught${parts}`;
    case "checking":
      return "Checking your deal terms made it in";
    case "saving":
      return "Saving the draft";
  }
}

function BuildStatus({ progress, startedAt }: { progress: BuildProgress | undefined; startedAt: string | undefined }): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const started = startedAt !== undefined ? Date.parse(startedAt) : NaN;
  const secs = Number.isNaN(started) ? null : Math.max(0, Math.floor((now - started) / 1000));
  const elapsed = secs === null ? "" : ` · ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  return <>{buildStageText(progress)}{elapsed}</>;
}

// [S-507] Inline confirmation form ("ask the user"): when the agent attaches an
// `elicit` request to a turn, render its prompt + fields beneath the bubble so
// the user can confirm or edit the auto-detected parameters before the tool
// runs. On Confirm we encode the values as a normal user message and submit it
// through the thread's send path; the agent reads it and proceeds. One-shot —
// the form disables itself after submitting (or dismissing).
function MessageElicitForm(): JSX.Element | null {
  const elicit = useMessage(
    (m) =>
      (m.metadata?.custom as { elicit?: Elicitation } | undefined)?.elicit
  );
  const submit = useElicitSubmit();
  const [edits, setEdits] = useState<Record<string, string>>({});
  // [S-769] A record pick answers with an ID, not a title: the drafts the agent
  // offers can share a title, and every tool that takes one resolves it by
  // EXACT match. Keyed by field name; the title is kept for what we show.
  const [picked, setPicked] = useState<Record<string, { id: string; title: string }>>({});
  const [pickerFor, setPickerFor] = useState<ElicitField | null>(null);
  // "open" | "submitted" | "dismissed" — the form is one-shot.
  const [state, setState] = useState<"open" | "submitted" | "dismissed">("open");

  if (elicit === undefined) return null;

  const valueFor = (f: ElicitField): string =>
    f.type === "draft"
      ? (picked[f.name]?.title ?? "")
      : (edits[f.name] ?? f.suggested ?? f.options?.[0] ?? "");

  /** `just` is a pick made in this very click: React has not flushed it into
   *  `picked` yet, so the answer is built from it directly. */
  const onConfirm = (just?: { field: string; record: { id: string; title: string } }): void => {
    // [chat c2535be4] The pick also rides as a real composer pick, so the server
    // grounds the next tool on the id instead of trusting the model to copy it.
    const records = elicit.fields
      .map((f) => (just?.field === f.name ? just.record : f.type === "draft" ? picked[f.name] : undefined))
      .filter((r): r is { id: string; title: string } => r !== undefined)
      .map((r) => ({ kind: "draft" as const, id: r.id, title: r.title }));
    const encoded =
      "Confirmed parameters — " +
      elicit.fields
        .map((f) => {
          const chosen = just?.field === f.name ? just.record : f.type === "draft" ? picked[f.name] : undefined;
          // The id rides along so the next tool takes `draftId` instead of
          // re-resolving a title string that two drafts can share.
          return chosen === undefined
            ? `${f.name}: ${valueFor(f)}`
            : `${f.name}: ${chosen.title} (id: ${chosen.id})`;
        })
        .join("; ");
    setState("submitted");
    submit(encoded, records.length > 0 ? { draftIds: records.map((r) => r.id), records } : undefined);
  };

  const disabled = state !== "open";

  return (
    <form
      className="sa-elicit"
      aria-label="Confirm parameters"
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled) onConfirm();
      }}
    >
      <div className="sa-elicit__prompt">
        <span className="src-dot" aria-hidden="true" />
        {elicit.prompt}
      </div>
      <div className="sa-elicit__fields">
        {elicit.fields.map((f) => {
          const id = `elicit-${f.name}`;
          return (
            <div key={f.name} className="sa-elicit__field">
              {f.type === "draft" ? (
                <>
                  <span className="sa-elicit__label">{f.label}</span>
                  {/* [S-769] The SAME picker the composer's Draft button opens
                      — search, dates, deal type, the detail panel — instead of
                      a dropdown of titles. It answers with a record, so the
                      confirmation carries an id. */}
                  <Button
                    variant="secondary"
                    className="sa-elicit__input"
                    disabled={disabled}
                    onClick={() => setPickerFor(f)}
                    aria-label={picked[f.name] === undefined ? "Choose a draft from the Workspace" : `Chosen: ${picked[f.name]!.title}. Choose a different draft`}
                  >
                    {picked[f.name]?.title ?? "📄 Choose a draft…"}
                  </Button>
                </>
              ) : f.type === "select" ? (
                <>
                  {/* Select supplies its own aria-label; this is the visible
                      caption. */}
                  <span className="sa-elicit__label">{f.label}</span>
                  <Select
                    label={f.label}
                    value={valueFor(f)}
                    clearable={false}
                    disabled={disabled}
                    triggerClassName="sa-elicit__input sa-elicit__trigger"
                    options={(f.options ?? []).map((opt) => ({
                      value: opt,
                      label: pretty(opt),
                    }))}
                    onValueChange={(v) =>
                      setEdits((prev) => ({ ...prev, [f.name]: v ?? "" }))
                    }
                  />
                </>
              ) : (
                <>
                  <label className="sa-elicit__label" htmlFor={id}>
                    {f.label}
                  </label>
                  <input
                    id={id}
                    className="sa-elicit__input"
                    type="text"
                    value={valueFor(f)}
                    disabled={disabled}
                    onChange={(e) =>
                      setEdits((prev) => ({ ...prev, [f.name]: e.target.value }))
                    }
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* [S-769] The draft picker this form opens for a record-pick field —
          outside the open/submitted branch, so it closes cleanly either way. */}
      <ContextPicker
        kind={pickerFor === null ? null : "draft"}
        {...(pickerFor?.documentForm === "deal_memo"
          ? { initialFilter: "Deal memo" }
          : pickerFor?.documentForm === "long_form"
            ? { initialFilter: "Long form" }
            : {})}
        onClose={() => setPickerFor(null)}
        onSelect={(_kind, record) => {
          if (pickerFor === null) return;
          const chosen = { id: record.id, title: record.title };
          setPicked((prev) => ({ ...prev, [pickerFor.name]: chosen }));
          setPickerFor(null);
          // [S-769] Picking IS the answer when the draft is the only thing
          // asked: the agent asked one question, so one gesture answers it and
          // the turn continues (Diego, 2026-09-26 — "it should confirm the
          // selection and the chatbot should go next"). A form that asks for
          // more than this still waits for Confirm.
          if (elicit.fields.length === 1) onConfirm({ field: pickerFor.name, record: chosen });
        }}
      />
      {state === "open" ? (
        <div className="sa-elicit__actions">
          <Button type="submit" variant="primary" className="sa-elicit__confirm">
            Confirm
          </Button>
          <Button
            type="button"
            variant="bare"
            className="sa-elicit__cancel"
            onClick={() => setState("dismissed")}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="sa-elicit__done" role="status">
          {state === "submitted" ? "Confirmed." : "Dismissed."}
        </div>
      )}
    </form>
  );
}

// "feature_film" -> "Feature Film".
function pretty(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Reshape a RecordRef into the card's structured fields. The subtitle today is
// "<dealType> · <guild>"; split it into a heading + guild chip. When the record
// has no real name (the backend appends a generic "Untitled …"), lead with the
// prettified deal type instead of printing it twice. The kind label is driven by
// the record TYPE — which also picks the row's layer color (precedent=L2 purple,
// template=L1 blue, draft=neutral — not a retrieval layer).
export function refFields(r: RecordRef): {
  title: string;
  chips: string[];
  kind: string;
} {
  const parts = (r.subtitle ?? "")
    .split("·")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  const dealType = parts[0];
  const guild = parts[1];
  const generic = /^untitled\b/i.test(r.title);
  const title = generic && dealType !== undefined ? pretty(dealType) : r.title;
  const chips = guild !== undefined ? [guild] : [];
  const kind = refKind(r.type);
  return { title, chips, kind };
}

function refKind(type: RecordRef["type"]): string {
  switch (type) {
    case "template":
      return "Certified template";
    case "draft":
      return "Draft";
    case "precedent":
      return "Precedent";
    case "attachment":
      return "Attachment";
    case "chat_message":
      return "Chat message";
    case "review":
      return "Review";
  }
}

function refKindShort(type: RecordRef["type"]): string {
  switch (type) {
    case "template":
      return "Certified";
    case "draft":
      return "Draft";
    case "precedent":
      return "Precedent";
    case "attachment":
      return "Attachment";
    case "chat_message":
      return "Message";
    case "review":
      return "Review";
  }
}

// Plural-friendly noun for the pill's breakdown text ("2 templates, 1
// precedent") — distinct from refKindShort's adjective-style row badge
// ("Certified"), which doesn't read naturally as a count noun on its own.
function refNoun(type: RecordRef["type"]): string {
  switch (type) {
    case "template":
      return "template";
    case "draft":
      return "draft";
    case "precedent":
      return "precedent";
    case "attachment":
      return "attachment";
    case "chat_message":
      return "message";
    case "review":
      return "review";
  }
}

function refRowClass(type: RecordRef["type"]): string {
  switch (type) {
    case "template":
      return "sa-refbar__row sa-refbar__row--template";
    case "draft":
      return "sa-refbar__row sa-refbar__row--draft";
    case "precedent":
      return "sa-refbar__row";
    case "attachment":
      return "sa-refbar__row";
    case "chat_message":
      return "sa-refbar__row";
    case "review":
      return "sa-refbar__row sa-refbar__row--template";
  }
}

// [S-413 follow-up] "Option 2" reference bar: a compact pill ("N sources
// used" + a small dot facepile) that unfurls into a footnote-style row list,
// replacing the old card grid. The numbers line up with the [n] markers the
// agent uses in prose (S-412) — MarkdownText renders those as clickable too.
// Raw ids are never shown — only titles. Clicking a row (or an inline [n])
// opens the SAME shared record drawer the Templates/Precedents/Drafts list
// screens use.
function MessageReferences(): JSX.Element | null {
  const refs = useMessage(
    (m) =>
      (m.metadata?.custom as { references?: RecordRef[] } | undefined)
        ?.references ?? []
  );
  const { openRecord } = useRecordDrawer();
  const [open, setOpen] = useState(false);
  if (refs.length === 0) return null;

  const counts = refs.reduce(
    (acc, r) => ({ ...acc, [r.type]: (acc[r.type] ?? 0) + 1 }),
    {} as Partial<Record<RecordRef["type"], number>>
  );
  const breakdown = (
    Object.entries(counts) as Array<[RecordRef["type"], number]>
  )
    .map(([type, n]) => `${n} ${refNoun(type)}${n === 1 ? "" : "s"}`)
    .join(", ");

  return (
    <div className="sa-refbar" aria-label="Referenced records">
      <Button
        variant="bare"
        className="sa-refbar__pill"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="sa-refbar__dots" aria-hidden="true">
          {refs.slice(0, 3).map((r) => (
            <span
              key={r.uri}
              className="sa-refbar__dot"
              style={
                {
                  "--sa-accent":
                    r.type === "template"
                      ? "var(--cs-source-cert)"
                      : r.type === "draft"
                        ? "var(--cs-border-strong)"
                        : "var(--cs-source-stud)",
                } as CSSProperties
              }
            />
          ))}
        </span>
        <span className="sa-refbar__count">
          {refs.length} source{refs.length === 1 ? "" : "s"} used
        </span>
        <span className="sa-refbar__breakdown">· {breakdown}</span>
        <span
          className={
            open ? "sa-refbar__chevron sa-refbar__chevron--open" : "sa-refbar__chevron"
          }
          aria-hidden="true"
        >
          ▸
        </span>
      </Button>
      {open && (
        <div className="sa-refbar__list">
          {refs.map((r, i) => {
            const f = refFields(r);
            return (
              <Button
                key={r.uri}
                variant="bare"
                className={refRowClass(r.type)}
                onClick={() => openRecord(r)}
                title={r.subtitle ?? r.title}
              >
                <span className="sa-refbar__row__n">{i + 1}</span>
                <span className="sa-refbar__row__title">{f.title}</span>
                {f.chips.map((c) => (
                  <span key={c} className="sa-refbar__row__chip">
                    {c}
                  </span>
                ))}
                <span className="sa-refbar__row__kind">{refKindShort(r.type)}</span>
                <span className="sa-refbar__row__open" aria-hidden="true">
                  ↗
                </span>
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// [S-413 follow-up] Wrap in-range "[n]" citation markers in markdownToHtml's
// (already-sanitized) output as clickable buttons, so a reader can open the
// referenced record straight from the sentence that cites it — not just from
// the reference bar below. `n` only becomes interactive when it actually
// indexes a record the agent attached to THIS turn (refs.length-bounded) —
// an out-of-range or coincidental "[12]" in prose (e.g. a citation to a
// clause number) is left as plain text, never guessed into a link. The title
// attribute alone gives the native "on mouse over, show the title" tooltip.
// Injected markup is safe by construction: the tag/attributes are fixed
// literals we write; the only interpolated values are a numeric index and an
// attribute-escaped title — nothing here re-opens the injection surface
// DOMPurify already closed upstream.
//
// [citation-gap fix, found live 2026-08-14] "[n]" was the ONLY thing this
// linked, but no persona's prompt ever instructs the model to write "[n]" —
// GROUNDING instead tells it to "refer to [a template] by title" (S-412's
// original design). Live: a real "create a new contract" session loaded 5
// sources and never showed a single inline citation, because the model did
// exactly what it was told — named the template by title, no brackets — and
// citeMarkdownHtml had nothing to grab onto. Rather than force the model to
// predict a bracket index it can't reliably know in advance (references
// accumulate and dedupe across an entire multi-step tool-calling turn, not
// just the one call that produced a given source — the model has no way to
// see where a source will land in that final array), this closes the gap
// from the renderer side: citeByTitle() below does the same linking by
// matching each ref's own displayed title against the prose, which is
// exactly what the model was already told to write. Zero prompt risk — no
// change to agentRegistry.ts, so no risk to S-667's routing corpus or any
// other prompt-dependent behavior.
function citeMarkdownHtml(html: string, refs: RecordRef[]): string {
  if (refs.length === 0) return html;
  const linked = new Set<number>();
  const bracketed = html.replace(/\[(\d+)\]/g, (whole, numStr: string) => {
    const n = Number(numStr);
    const ref = refs[n - 1];
    if (ref === undefined) return whole;
    linked.add(n - 1);
    const title = escapeHtmlAttr(refFields(ref).title);
    return `<button type="button" class="sa-cite" data-cite="${n - 1}" title="${title}">[${n}]</button>`;
  });
  return citeByTitle(bracketed, refs, linked);
}

// A title shorter than this is too likely to coincidentally appear as an
// ordinary word/phrase in unrelated prose (e.g. a 3-letter guild code) to
// safely auto-link — every real title observed live (templates, drafts,
// attachments) runs well past this, so the guard costs nothing in practice.
const MIN_CITABLE_TITLE_LENGTH = 8;

// [citation-gap fix] Link a ref's FIRST verbatim occurrence of its own
// display title in the (already bracket-processed) html, skipping any ref
// index already linked via an actual "[n]" marker — so a response mixing
// both styles never double-links the same source. Same "first occurrence
// only" spirit as RecordDrawer.tsx's highlightQuote. The match is a plain
// (regex-escaped) substring of the sanitized html string, same class of
// string-level risk citeMarkdownHtml already accepts for "[n]" — a title
// coincidentally landing inside an attribute/tag is astronomically
// unlikely for titles this specific and this long.
function citeByTitle(
  html: string,
  refs: RecordRef[],
  alreadyLinked: Set<number>
): string {
  let result = html;
  // Two DISTINCT refs sharing the exact same display title is common (e.g.
  // 5 search hits that all resolve to "Certified standard — feature_film ·
  // SAG · professional"). Once a title string has been linked once, never
  // search for it again: a second `.replace()` call for the same string
  // would re-scan `result`, find the <button> just inserted for the FIRST
  // match (its own text content IS that title), and wrap a second button
  // around it — nested/malformed markup (confirmed live: a garbled `title`
  // attribute). Longest-title-first also means a title that's a PREFIX of
  // another (e.g. "...SAG" vs "...SAG · professional") can't claim the
  // shared prefix before the more specific one gets a chance to match the
  // whole thing.
  const order = refs
    .map((ref, i) => ({ ref, i }))
    .filter(({ i }) => !alreadyLinked.has(i))
    .sort((a, b) => refFields(b.ref).title.length - refFields(a.ref).title.length);
  const usedTitles = new Set<string>();
  for (const { ref, i } of order) {
    const title = refFields(ref).title.trim();
    if (title.length < MIN_CITABLE_TITLE_LENGTH) continue;
    if (usedTitles.has(title)) continue;
    const pattern = new RegExp(title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!pattern.test(result)) continue;
    usedTitles.add(title);
    const titleAttr = escapeHtmlAttr(title);
    // sa-cite--title: the base .sa-cite badge styling (boxed, text-top) was
    // sized for a 2-3 char "[n]" — a whole title needs to read as an inline
    // link instead, not a badge wrapping a sentence fragment.
    result = result.replace(
      pattern,
      `<button type="button" class="sa-cite sa-cite--title" data-cite="${i}" title="${titleAttr}">${title}</button>`
    );
  }
  return result;
}

function escapeHtmlAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Render the assistant's streamed text as Markdown. The part updates on every
// delta, so this re-renders incrementally as tokens arrive.
function MarkdownText(): JSX.Element {
  const part = useMessagePartText();
  const refs = useMessage(
    (m) =>
      (m.metadata?.custom as { references?: RecordRef[] } | undefined)
        ?.references ?? []
  );
  const { openRecord } = useRecordDrawer();
  const html = useMemo(
    () => citeMarkdownHtml(markdownToHtml(part.text), refs),
    [part.text, refs]
  );
  // Event delegation: the citation buttons live inside raw injected HTML, so
  // they can't carry their own onClick — a single listener on the wrapper
  // resolves the clicked marker's index back to its RecordRef.
  const onClickCite = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-cite]");
      if (target === null) return;
      const ref = refs[Number(target.dataset["cite"])];
      if (ref !== undefined) openRecord(ref);
    },
    [refs, openRecord]
  );
  return (
    <div
      onClick={onClickCite}
      // Sanitized in markdownToHtml() via DOMPurify (see lib/markdown.ts);
      // citeMarkdownHtml() only adds fixed, self-authored markup on top.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
